<template>
  <div
    ref="dragContainer"
    style="max-width: 1600px"
    class="mx-auto px-2 sm:px-3 md:px-4 h-full flex flex-col overflow-hidden max-w-full"
    :class="{ 'cohort-programme-builder': isCohortProgramme, 'folio-builder': !isCohortProgramme }"
    @dragover="onDragover"
    @drop="isDragFile = false"
  >
    <HoverDropzone
      v-if="sortedDocuments.length && withUploadButton && editable"
      :is-dragging="isDragFile"
      :template-id="template.id"
      :accept-file-types="acceptFileTypes"
      :with-replace-and-clone="withReplaceAndCloneUpload"
      @add="[updateFromUpload($event), isDragFile = false]"
      @replace="[onDocumentsReplace($event), isDragFile = false]"
      @replace-and-clone="onDocumentsReplaceAndTemplateClone($event)"
      @error="[onUploadFailed($event), isDragFile = false]"
    />
    <DragPlaceholder
      ref="dragPlaceholder"
      :field="fieldsDragFieldRef.value || toRaw(dragField)"
      :is-field="template.fields.includes(fieldsDragFieldRef.value)"
      :is-default="defaultFields.includes(toRaw(dragField))"
      :is-required="defaultRequiredFields.includes(toRaw(dragField))"
    />
    <div
      v-if="pendingFieldAttachmentUuids.length && editable"
      class="top-1.5 sticky h-0 z-20 max-w-2xl mx-auto"
    >
      <div class="alert border-base-content/30 py-2 px-2.5">
        <IconInfoCircle
          class="stroke-info shrink-0 w-6 h-6"
        />
        <span>{{ t('uploaded_pdf_contains_form_fields_keep_or_remove_them') }}</span>
        <div>
          <button
            class="btn btn-sm"
            @click.prevent="removePendingFields"
          >
            {{ t('remove') }}
          </button>
          <button
            class="btn btn-sm btn-neutral text-white"
            @click.prevent="save"
          >
            {{ t('keep') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="$slots.buttons || withTitle"
      id="title_container"
      class="flex justify-between items-center w-full mt-2 mb-3 md:mt-[18px] md:mb-[34px] shrink-0 title-container relative z-[100]"
      :class="{ sticky: withStickySubmitters || isBreakpointLg }"
      :style="{ backgroundColor }"
    >
      <div class="flex items-center gap-2 md:gap-[22px] min-w-0 flex-1">
        <a
          v-if="withLogo"
          href="/"
          class="flex items-center justify-center shrink-0 no-underline w-[36px] h-[36px] md:w-[48px] md:h-[48px] rounded-[10px] md:rounded-[14px] overflow-hidden"
        >
          <Logo />
        </a>
        <Contenteditable
          v-if="withTitle"
          :model-value="template.name"
          :editable="editable"
          class="font-['Raleway'] text-[14px] sm:text-[18px] md:text-[28px] font-bold text-black template-name outline-none min-w-0 max-w-[100px] sm:max-w-[180px] md:max-w-[20ch] truncate"
          :title="template.name"
          :icon-stroke-width="1.3"
          :max-length="20"
          :truncate="true"
          @update:model-value="updateName"
        />
        <div
          v-if="programmes.length > 0"
          id="header_program_switcher"
          class="ml-1 sm:ml-3 flex items-center shrink-0"
        >
          <div class="dropdown">
            <label
              tabindex="0"
              class="btn btn-xs sm:btn-sm bg-transparent rounded-[8px] border-[1.5px] border-[#01d680] text-[#043f42] hover:bg-[#f8fff8] hover:border-[#01d680] normal-case font-bold px-2 py-1 flex items-center gap-1 cursor-pointer"
            >
              <span class="hidden sm:inline">Switch Programs</span>
              <span class="sm:hidden text-xs">Programs</span>
              <IconChevronDown class="w-[12px] h-[12px] sm:w-[14px] sm:h-[14px] text-[#01d680]" />
            </label>
            <ul
              tabindex="0"
              class="dropdown-content z-[150] menu p-2 shadow-xl bg-white rounded-box w-52 sm:w-56 text-[14px] border border-gray-100"
            >
              <li
                v-for="prog in programmes"
                :key="prog.id"
              >
                <a
                  :href="`/cohorts/${cohortId}/programmes/${prog.id}/mapping`"
                  :class="{ 'active font-bold text-[#01d680]': prog.id === programmeId }"
                >
                  {{ truncate(prog.name, 20) }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="gap-2 md:gap-[13px] flex items-center flex-shrink-0">
        <slot
          v-if="$slots.buttons"
          name="buttons"
        />
        <template v-else>
          <form
            v-if="withSignYourselfButton && template.submitters.length < 2 && !template.is_reusable"
            ref="selfSignForm"
            target="_blank"
            data-turbo="false"
            class="hidden md:inline"
            method="post"
            :action="`/d/${template.slug}`"
            @submit="maybeShowErrorTemplateAlert"
          >
            <input
              type="hidden"
              name="_method"
              value="put"
              autocomplete="off"
            >
            <input
              type="hidden"
              name="authenticity_token"
              :value="authenticityToken"
              autocomplete="off"
            >
            <input
              type="hidden"
              name="selfsign"
              value="true"
              autocomplete="off"
            >
            <button
              class="flex items-center justify-center bg-[#01d680] hover:bg-[#00c576] rounded-[38px] px-[10px] py-[10px] gap-[10px] text-black font-['Raleway'] text-[14px] font-normal border-none cursor-pointer leading-[1.4]"
              type="submit"
            >
              <IconWritingSign class="w-[14px] h-[14px] inline" />
              <span class="hidden md:inline">
                {{ signYourselfText || 'Sign as Training Provider' }}
              </span>
            </button>
          </form>
          <button
            v-else-if="withSignYourselfButton && !template.is_reusable"
            id="sign_yourself_button"
            type="button"
            class="hidden md:flex items-center justify-center bg-[#01d680] hover:bg-[#00c576] rounded-[38px] px-[10px] py-[10px] gap-[10px] text-black font-['Raleway'] text-[14px] font-normal border-none cursor-pointer no-underline leading-[1.4]"
            @click.prevent="onSignFolioClick"
          >
            <IconWritingSign class="w-[14px] h-[14px] inline" />
            <span class="hidden md:inline">
              {{ signYourselfText || 'Sign as Training Provider' }}
            </span>
          </button>
          <template v-if="withSendButton">
            <a
              v-if="!template.is_reusable"
              id="send_button"
              :href="`/documents/${template.id}/submissions/new?with_link=true`"
              data-turbo-frame="modal"
              class="hidden md:flex items-center justify-center bg-[#01d680] hover:bg-[#00c576] rounded-[38px] px-[10px] py-[10px] gap-[10px] text-black font-['Raleway'] text-[14px] font-normal border-none cursor-pointer no-underline leading-[1.4]"
              @click="maybeShowErrorTemplateAlert"
            >
              <IconUsersPlus class="w-[14px] h-[14px] inline" />
              <span class="hidden md:inline">{{ t('send') }}</span>
            </a>
            <a
              v-else
              id="use_template_button"
              :href="`/templates/${template.id}/clone?spawn=true`"
              data-turbo-method="post"
              class="hidden md:flex items-center justify-center bg-[#01d680] hover:bg-[#00c576] rounded-[38px] px-[10px] py-[10px] gap-[10px] text-black font-['Raleway'] text-[14px] font-normal border-none cursor-pointer no-underline leading-[1.4]"
              @click="maybeShowErrorTemplateAlert"
            >
              <IconSend class="w-[14px] h-[14px] inline" />
              <span class="hidden md:inline">{{ t('use_template', 'Use Template') }}</span>
            </a>
          </template>
          <span
            v-if="editable"
            id="save_button_container"
            class="inline-flex items-center bg-[#01d680] hover:bg-[#00c576] rounded-[38px] shadow-xs relative transition-colors"
          >
            <button
              class="h-[38px] px-3 md:px-3.5 flex items-center justify-center gap-1.5 text-black font-['Raleway'] text-[14px] font-medium border-none cursor-pointer leading-none rounded-l-[38px] hover:bg-black/5 transition-colors"
              :class="{ 'opacity-50 !cursor-not-allowed': isSaving }"
              :disabled="isSaving"
              @click.prevent="onSaveClick"
            >
              <IconInnerShadowTop
                v-if="isSaving"
                class="w-[15px] h-[15px] animate-spin shrink-0"
              />
              <IconDeviceFloppy
                v-else
                class="w-[15px] h-[15px] shrink-0"
              />
              <span class="hidden md:inline">
                {{ t('save') }}
              </span>
            </button>
            <div
              class="dropdown dropdown-bottom dropdown-end flex items-center"
              :class="{ 'dropdown-open': isSaveDropdownOpen || isDownloading }"
            >
              <button
                id="save_dropdown_trigger"
                type="button"
                tabindex="0"
                class="h-[38px] px-2 md:px-2.5 flex items-center justify-center text-black border-l border-black/15 cursor-pointer leading-none rounded-r-[38px] hover:bg-black/5 transition-colors bg-transparent border-t-0 border-b-0 border-r-0"
                @click.stop="isSaveDropdownOpen = !isSaveDropdownOpen"
              >
                <IconChevronDown class="w-[14px] h-[14px] shrink-0" />
              </button>
              <ul
                tabindex="0"
                class="dropdown-content z-[150] p-2 mt-2 shadow-lg bg-white rounded-[11px] text-gray-700 font-['Raleway'] text-[14px] min-w-[220px] border border-gray-100 flex flex-col gap-1"
              >
                <!-- Mobile only items in dropdown -->
                <li
                  v-if="withSignYourselfButton && !template.is_reusable"
                  class="md:hidden"
                >
                  <a
                    href="#"
                    class="flex items-center space-x-3 hover:bg-gray-50 px-3 py-2 rounded-[8px] no-underline text-gray-700 font-medium cursor-pointer"
                    @click.prevent="triggerMobileSelfSign"
                  >
                    <IconWritingSign class="w-[18px] h-[18px] flex-shrink-0 text-[#01d680]" />
                    <span class="whitespace-nowrap">{{ signYourselfText || 'Sign as Training Provider' }}</span>
                  </a>
                </li>
                <li
                  v-if="withSendButton"
                  class="md:hidden"
                >
                  <a
                    v-if="!template.is_reusable"
                    :href="`/documents/${template.id}/submissions/new?with_link=true`"
                    data-turbo-frame="modal"
                    class="flex items-center space-x-3 hover:bg-gray-50 px-3 py-2 rounded-[8px] no-underline text-gray-700 font-medium cursor-pointer"
                    @click="maybeShowErrorTemplateAlert"
                  >
                    <IconUsersPlus class="w-[18px] h-[18px] flex-shrink-0 text-[#01d680]" />
                    <span class="whitespace-nowrap">{{ t('send') }}</span>
                  </a>
                  <a
                    v-else
                    :href="`/templates/${template.id}/clone?spawn=true`"
                    data-turbo-method="post"
                    class="flex items-center space-x-3 hover:bg-gray-50 px-3 py-2 rounded-[8px] no-underline text-gray-700 font-medium cursor-pointer"
                    @click="maybeShowErrorTemplateAlert"
                  >
                    <IconSend class="w-[18px] h-[18px] flex-shrink-0 text-[#01d680]" />
                    <span class="whitespace-nowrap">{{ t('use_template', 'Use Template') }}</span>
                  </a>
                </li>
                <li
                  v-if="(withSignYourselfButton && !template.is_reusable) || withSendButton"
                  class="md:hidden border-t border-gray-100 my-1"
                />
                <li>
                  <a
                    :href="saveAndPreviewHref"
                    data-turbo="false"
                    class="flex items-center space-x-3 hover:bg-gray-50 px-3 py-2 rounded-[8px] no-underline text-gray-700 transition-colors cursor-pointer"
                    @click="onSaveAndPreviewClick"
                  >
                    <IconEye class="w-[18px] h-[18px] flex-shrink-0 text-gray-500" />
                    <span class="whitespace-nowrap font-medium">{{ t('save_and_preview') }}</span>
                  </a>
                </li>
                <li
                  v-if="!isCohortProgramme"
                  class="border-t border-gray-100 my-1"
                />
                <li
                  v-if="!isCohortProgramme"
                  @click.stop
                >
                  <label
                    class="flex items-center justify-between px-3 py-2 rounded-[8px] hover:bg-gray-50 cursor-pointer select-none space-x-3"
                    @click.stop
                  >
                    <div class="flex items-center space-x-2">
                      <IconListNumbers class="w-[18px] h-[18px] flex-shrink-0 text-gray-500" />
                      <span class="whitespace-nowrap font-medium text-[13px] text-gray-700">Sequential signing</span>
                    </div>
                    <input
                      type="checkbox"
                      class="toggle toggle-sm toggle-success cursor-pointer"
                      :checked="isSequentialSigning"
                      @change="onToggleSequentialSigning($event.target.checked)"
                    >
                  </label>
                </li>
              </ul>
            </div>
          </span>
        </template>
      </div>
    </div>
    <div
      id="main_container"
      class="flex main-container flex-1 min-h-0"
    >
      <div
        v-if="withDocumentsList"
        id="documents_container"
        ref="previews"
        :style="{ 'display': isBreakpointLg ? 'none' : 'flex' }"
        class="flex flex-col w-52 flex-none pr-3 mt-0.5 pt-0.5 hidden lg:flex h-full overflow-hidden"
      >
        <div class="flex-1 overflow-y-auto overflow-x-hidden doccu-scrollbar pr-2 max-h-[500px]">
          <DocumentPreview
            v-for="(item, index) in template.schema"
            :key="index"
            :with-arrows="template.schema.length > 1"
            :item="item"
            :document="sortedDocuments[index]"
            :data-document-uuid="item.attachment_uuid"
            :accept-file-types="acceptFileTypes"
            :with-replace-button="withUploadButton"
            :editable="editable"
            :template="template"
            @scroll-to="scrollIntoDocument(item)"
            @remove="onDocumentRemove"
            @replace="onDocumentReplace"
            @up="moveDocument(item, -1)"
            @reorder="reorderFields"
            @down="moveDocument(item, 1)"
            @change="save"
          />
        </div>
        <div
          class="shrink-0 py-2 space-y-2 mt-2"
          :style="{ backgroundColor }"
        >
          <Upload
            v-if="editable && withUploadButton"
            v-show="sortedDocuments.length"
            ref="upload"
            :accept-file-types="acceptFileTypes"
            :authenticity-token="authenticityToken"
            :with-google-drive="withGoogleDrive"
            :template-id="template.id"
            @success="updateFromUpload"
          />
          <button
            v-if="sortedDocuments.length && editable && withAddPageButton"
            id="add_blank_page_button"
            class="btn btn-outline w-full add-blank-page-button"
            @click.prevent="addBlankPage"
          >
            <IconInnerShadowTop
              v-if="isLoadingBlankPage"
              class="animate-spin w-5 h-5"
            />
            <IconPlus
              v-else
              class="w-5 h-5"
            />
            {{ t('add_blank_page') }}
          </button>
        </div>
      </div>
      <div class="relative flex-1 min-w-0 h-full flex flex-col">
        <!-- Floating PDF Zoom Controls -->
        <div
          v-if="sortedDocuments.length"
          id="pdf_zoom_controls"
          class="absolute top-2 right-2 md:top-3 md:right-4 z-20 flex items-center bg-white/95 backdrop-blur-xs border border-gray-200/90 shadow-md rounded-full px-2 py-1 gap-1 select-none"
        >
          <button
            id="zoom_out_button"
            type="button"
            class="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#043f42] text-xs font-bold border-none cursor-pointer bg-transparent transition-colors"
            title="Zoom out"
            :disabled="zoomLevel <= 50"
            :class="{ 'opacity-40 cursor-not-allowed': zoomLevel <= 50 }"
            @click.prevent="zoomOut"
          >
            <IconMinus class="w-3.5 h-3.5" />
          </button>
          <button
            id="zoom_reset_button"
            type="button"
            class="text-[11px] font-bold text-[#043f42] hover:text-[#01d680] px-1 py-0.5 border-none cursor-pointer bg-transparent transition-colors"
            title="Reset zoom to 100%"
            @click.prevent="resetZoom"
          >
            {{ zoomLevel }}%
          </button>
          <button
            id="zoom_in_button"
            type="button"
            class="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#043f42] text-xs font-bold border-none cursor-pointer bg-transparent transition-colors"
            title="Zoom in"
            :disabled="zoomLevel >= 200"
            :class="{ 'opacity-40 cursor-not-allowed': zoomLevel >= 200 }"
            @click.prevent="zoomIn"
          >
            <IconPlus class="w-3.5 h-3.5" />
          </button>
        </div>

        <div
          id="pages_container"
          class="w-full flex-1 min-h-0 mt-0.5 pt-0.5 scrollbar-hide overflow-y-auto"
          :class="{ 'overflow-x-auto': zoomLevel > 100, 'overflow-x-hidden': zoomLevel <= 100 }"
          @scroll="onPagesScroll"
        >
          <div
            ref="documents"
            class="pr-3.5 pl-0.5"
            :style="documentsZoomStyle"
          >
            <template v-if="!sortedDocuments.length && (withUploadButton || withAddPageButton)">
              <Dropzone
                v-if="withUploadButton"
                :template-id="template.id"
                :accept-file-types="acceptFileTypes"
                :with-google-drive="withGoogleDrive"
                @click-google-drive="$refs.upload.openGoogleDriveModal()"
                @success="updateFromUpload"
              />
              <button
                v-if="withAddPageButton"
                id="add_blank_page_button"
                class="btn btn-outline w-full mt-4 add-blank-page-button"
                @click.prevent="addBlankPage"
              >
                <IconInnerShadowTop
                  v-if="isLoadingBlankPage"
                  class="animate-spin w-5 h-5"
                />
                <IconPlus
                  v-else
                  class="w-5 h-5"
                />
                {{ t('add_blank_page') }}
              </button>
            </template>
            <template v-else>
              <template
                v-for="document in sortedDocuments"
                :key="document.uuid"
              >
                <Document
                  :ref="setDocumentRefs"
                  :areas-index="fieldAreasIndex[document.uuid]"
                  :selected-submitter="selectedSubmitter"
                  :document="document"
                  :is-drag="!!dragField"
                  :input-mode="inputMode"
                  :default-fields="[...defaultRequiredFields, ...defaultFields]"
                  :allow-draw="!onlyDefinedFields || drawField"
                  :with-signature-id="withSignatureId"
                  :with-prefillable="withPrefillable"
                  :data-document-uuid="document.uuid"
                  :default-submitters="defaultSubmitters"
                  :drag-field-placeholder="fieldsDragFieldRef.value || dragField"
                  :with-field-placeholder="withFieldPlaceholder"
                  :draw-field="drawField"
                  :draw-field-type="drawFieldType"
                  :editable="editable"
                  :base-url="baseUrl"
                  :with-fields-detection="withFieldsDetection"
                  @draw="[onDraw($event), withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false, cancelMobileDraw()]"
                  @drop-field="onDropfield"
                  @remove-area="removeArea"
                  @paste-field="pasteField"
                  @copy-field="copyField"
                  @copy-selected-areas="copySelectedAreas"
                  @delete-selected-areas="deleteSelectedAreas"
                  @align-selected-areas="alignSelectedAreas"
                  @autodetect-fields="detectFieldsForPage"
                />
              </template>
            </template>
          </div>
        </div>

        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="translate-y-10 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="translate-y-0 opacity-100"
          leave-to-class="translate-y-10 opacity-0"
        >
          <button
            v-show="showScrollTopFab"
            class="absolute bottom-8 right-8 z-[100] w-[48px] h-[48px] bg-[#22ec9b] hover:bg-[#01d680] text-[#043f42] rounded-full shadow-lg flex items-center justify-center transition-all cursor-pointer border-none shadow-[0_4px_14px_0_rgba(34,236,155,0.39)]"
            title="Scroll to top"
            @click.prevent="scrollToTop"
          >
            <IconArrowUp class="w-6 h-6" />
          </button>
        </Transition>
      </div>
      <div
        v-if="withFieldsList && !isMobile"
        id="fields_list_container"
        class="relative w-80 flex-none mt-1 hidden md:block fields-list-container flex flex-col h-full overflow-hidden"
      >
        <div
          v-if="showDrawField || drawField"
          class="sticky inset-0 h-full z-20"
          :style="{ backgroundColor }"
        >
          <div class="bg-base-200 rounded-lg p-5 text-center space-y-4 draw-field-container">
            <p v-if="(drawField?.type || drawFieldType) === 'strikethrough'">
              {{ t('draw_strikethrough_the_document') }}
            </p>
            <p v-else>
              {{ t('draw_field_on_the_document') }}
            </p>
            <div>
              <button
                class="base-button cancel-draw-button"
                @click="clearDrawField"
              >
                {{ t('cancel') }}
              </button>
              <a
                v-if="!drawField && !drawOption && !['signature', 'initials', 'heading', 'strikethrough'].includes(drawField?.type || drawFieldType)"
                href="#"
                class="link block mt-3 text-sm"
                @click.prevent="[addField(drawFieldType), drawField = null, drawOption = null, withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false]"
              >
                {{ t('or_add_field_without_drawing') }}
              </a>
            </div>
          </div>
        </div>
        <div class="flex flex-col flex-1 min-h-0 h-full">
          <Fields
            ref="fields"
            class="flex flex-col flex-1 min-h-0 h-full"
            :fields="template.fields"
            :submitters="template.submitters"
            :selected-submitter="selectedSubmitter"
            :with-help="withHelp"
            :default-submitters="defaultSubmitters"
            :draw-field-type="drawFieldType"
            :with-fields-search="withFieldsSearch"
            :default-fields="[...defaultRequiredFields, ...defaultFields]"
            :template="template"
            :default-required-fields="defaultRequiredFields"
            :field-types="fieldTypes"
            :cohort-id="cohortId"
            :with-sticky-submitters="withStickySubmitters"
            :with-fields-detection="withFieldsDetection"
            :with-signature-id="withSignatureId"
            :with-prefillable="withPrefillable"
            :only-defined-fields="onlyDefinedFields"
            :editable="editable"
            :show-tour-start-form="showTourStartForm"
            @add-field="addField"
            @set-draw="[drawField = $event.field, drawOption = $event.option]"
            @select-submitter="selectedSubmitter = $event"
            @change-submitter="selectedSubmitter = $event"
            @set-draw-type="[drawFieldType = $event, showDrawField = true]"
            @set-drag="dragField = $event"
            @set-drag-placeholder="$refs.dragPlaceholder.dragPlaceholder = $event"
            @edit-role="handleEditRole"
            @hide-dropdowns="[drawField = null, drawOption = null]"
            @drag-end="[dragField = null, $refs.dragPlaceholder.dragPlaceholder = null]"
            @scroll-to-area="scrollToArea"
          />
        </div>
      </div>
    </div>
    <!-- Mobile Left Drawer: Documents & Pages -->
    <div
      v-if="isLeftDrawerOpen"
      id="left_documents_drawer"
      class="fixed inset-0 z-[200] lg:hidden flex"
    >
      <div
        class="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
        @click="isLeftDrawerOpen = false"
      />
      <div class="relative w-[85%] max-w-[320px] bg-[#f8faf9] h-full shadow-2xl flex flex-col z-10 overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
          <div class="flex items-center space-x-2">
            <IconFiles class="w-5 h-5 text-[#043f42]" />
            <span class="font-['Raleway'] font-bold text-[#043f42] text-[16px]">Documents & Pages</span>
          </div>
          <button
            id="close_left_drawer_button"
            type="button"
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors cursor-pointer border-none"
            @click="isLeftDrawerOpen = false"
          >
            <IconX class="w-5 h-5" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-4 space-y-4 doccu-scrollbar">
          <div class="space-y-3">
            <DocumentPreview
              v-for="(item, index) in template.schema"
              :key="index"
              :with-arrows="template.schema.length > 1"
              :item="item"
              :document="sortedDocuments[index]"
              :data-document-uuid="item.attachment_uuid"
              :accept-file-types="acceptFileTypes"
              :with-replace-button="withUploadButton"
              :editable="editable"
              :template="template"
              @scroll-to="[scrollIntoDocument(item), isLeftDrawerOpen = false]"
              @remove="onDocumentRemove"
              @replace="onDocumentReplace"
              @up="moveDocument(item, -1)"
              @reorder="reorderFields"
              @down="moveDocument(item, 1)"
              @change="save"
            />
          </div>
          <div
            v-if="editable && withUploadButton"
            class="pt-3 border-t border-gray-200 space-y-2 shrink-0"
          >
            <Upload
              v-show="sortedDocuments.length"
              ref="uploadMobile"
              :accept-file-types="acceptFileTypes"
              :authenticity-token="authenticityToken"
              :with-google-drive="withGoogleDrive"
              :template-id="template.id"
              @success="updateFromUpload"
            />
            <button
              v-if="sortedDocuments.length && withAddPageButton"
              id="mobile_add_blank_page_button"
              class="btn btn-outline w-full add-blank-page-button"
              @click.prevent="addBlankPage"
            >
              <IconInnerShadowTop
                v-if="isLoadingBlankPage"
                class="animate-spin w-5 h-5"
              />
              <IconPlus
                v-else
                class="w-5 h-5"
              />
              {{ t('add_blank_page') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Right Drawer: Fields & Roles -->
    <div
      v-if="isRightDrawerOpen"
      id="right_fields_drawer"
      class="fixed inset-0 z-[200] lg:hidden flex justify-end"
    >
      <div
        class="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
        @click="isRightDrawerOpen = false"
      />
      <div class="relative w-[88%] max-w-[340px] bg-[#f8faf9] h-full shadow-2xl flex flex-col z-10 overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
          <div class="flex items-center space-x-2">
            <IconPlus class="w-5 h-5 text-[#01d680]" />
            <span class="font-['Raleway'] font-bold text-[#043f42] text-[16px]">Fields & Roles</span>
          </div>
          <button
            id="close_right_drawer_button"
            type="button"
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors cursor-pointer border-none"
            @click="isRightDrawerOpen = false"
          >
            <IconX class="w-5 h-5" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-3 doccu-scrollbar">
          <Fields
            class="flex flex-col h-full"
            :fields="template.fields"
            :submitters="template.submitters"
            :selected-submitter="selectedSubmitter"
            :with-help="false"
            :default-submitters="defaultSubmitters"
            :draw-field-type="drawFieldType"
            :with-fields-search="withFieldsSearch"
            :default-fields="[...defaultRequiredFields, ...defaultFields]"
            :template="template"
            :default-required-fields="defaultRequiredFields"
            :field-types="fieldTypes"
            :cohort-id="cohortId"
            :with-sticky-submitters="withStickySubmitters"
            :with-fields-detection="withFieldsDetection"
            :with-signature-id="withSignatureId"
            :with-prefillable="withPrefillable"
            :only-defined-fields="onlyDefinedFields"
            :editable="editable"
            :show-tour-start-form="showTourStartForm"
            @add-field="[addField($event), isRightDrawerOpen = false]"
            @set-draw="[drawField = $event.field, drawOption = $event.option, isRightDrawerOpen = false]"
            @select-submitter="selectedSubmitter = $event"
            @change-submitter="selectedSubmitter = $event"
            @set-draw-type="[drawFieldType = $event, showDrawField = true, isRightDrawerOpen = false]"
            @set-drag="dragField = $event"
            @set-drag-placeholder="$refs.dragPlaceholder.dragPlaceholder = $event"
            @edit-role="handleEditRole"
            @hide-dropdowns="[drawField = null, drawOption = null]"
            @drag-end="[dragField = null, $refs.dragPlaceholder.dragPlaceholder = null]"
            @scroll-to-area="[scrollToArea($event), isRightDrawerOpen = false]"
          />
        </div>
      </div>
    </div>

    <!-- Mobile Twin Floating Action Buttons -->
    <div
      v-if="!isLeftDrawerOpen && !isRightDrawerOpen && !isDrawingActive"
      class="lg:hidden"
    >
      <!-- Left Documents FAB -->
      <button
        v-if="withDocumentsList && sortedDocuments.length"
        id="mobile_documents_fab"
        type="button"
        class="fixed bottom-[22px] left-4 z-30 w-11 h-11 rounded-full bg-[#043f42] text-white shadow-xl flex items-center justify-center border border-white/20 active:scale-95 transition-all cursor-pointer"
        title="Documents & Pages"
        @click.prevent="[isLeftDrawerOpen = true, isRightDrawerOpen = false]"
      >
        <IconFiles class="w-5 h-5 text-white" />
      </button>

      <!-- Right Fields FAB -->
      <button
        v-if="withFieldsList && sortedDocuments.length && editable"
        id="mobile_fields_fab"
        type="button"
        class="fixed bottom-[22px] right-4 z-30 w-11 h-11 rounded-full bg-[#01d680] text-[#043f42] shadow-xl flex items-center justify-center border border-black/10 active:scale-95 transition-all cursor-pointer"
        title="Fields & Roles"
        @click.prevent="[isRightDrawerOpen = true, isLeftDrawerOpen = false]"
      >
        <IconPlus class="w-6 h-6 text-[#043f42]" />
      </button>
    </div>

    <!-- Mobile Draw Mode Center Docked Pill -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="translate-y-4 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-4 opacity-0"
    >
      <div
        v-if="isDrawingActive && (isBreakpointLg || isMobile)"
        id="mobile_draw_field_pill"
        class="fixed bottom-[22px] left-1/2 -translate-x-1/2 z-40 lg:hidden flex items-center gap-2 bg-[#043f42] text-white px-3.5 py-2 rounded-full shadow-2xl border border-[#01d680]/30 max-w-[280px]"
      >
        <div class="flex items-center gap-1.5 shrink-0">
          <IconPencil class="w-4 h-4 text-[#01d680] shrink-0" />
          <span class="text-xs font-semibold whitespace-nowrap text-white">
            Draw {{ currentDrawFieldName }}
          </span>
        </div>
        <button
          id="add_directly_mobile_button"
          type="button"
          class="text-[11px] bg-[#01d680] text-[#043f42] font-bold px-2 py-0.5 rounded-full hover:bg-[#00c576] transition-colors shrink-0 cursor-pointer border-none"
          title="Add field directly to current page"
          @click.prevent="addDirectlyOnCurrentPage(drawFieldType)"
        >
          + Add
        </button>
        <button
          id="cancel_mobile_draw_button"
          type="button"
          class="text-[11px] bg-white/20 hover:bg-white/30 text-white font-medium px-2 py-0.5 rounded-full transition-colors shrink-0 cursor-pointer border-none flex items-center gap-0.5"
          @click.prevent="cancelMobileDraw"
        >
          <IconX class="w-3.5 h-3.5" />
          Cancel
        </button>
      </div>
    </Transition>
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="translate-y-4 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-4 opacity-0"
    >
      <div
        v-if="isDetectingPageFields || detectingFieldsAddedCount !== null"
        class="sticky bottom-0 z-50"
      >
        <div class="absolute left-0 right-0 h-0 overflow-visible bottom-16 z-50 flex justify-center">
          <div
            class="rounded-full bg-base-content h-12 flex items-center justify-center space-x-1.5 uppercase font-semibold text-white text-sm cursor-default"
            style="min-width: 180px"
          >
            <template v-if="detectingFieldsAddedCount !== null">
              <span>{{ (detectingFieldsAddedCount === 1 ? t('field_added') : t('fields_added')).replace('{count}', detectingFieldsAddedCount) }}</span>
            </template>
            <template v-else>
              <IconInnerShadowTop
                v-if="!detectingAnalyzingProgress"
                width="20"
                class="animate-spin"
              />
              <span v-if="detectingAnalyzingProgress">
                {{ Math.round(detectingAnalyzingProgress * 100) }}% {{ t('analyzing_') }}
              </span>
              <span v-else>
                {{ t('processing_') }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </Transition>
    <div
      id="docuseal_modal_container"
      class="modal-container"
    />
    <ManageParticipantsModal
      v-if="isCohortProgramme"
      ref="participantsModal"
      :cohort-id="cohortId"
      :programme-id="programmeId"
      :role="participantRoleToManage"
      @saved="onParticipantsSaved"
    />
    <FolioSignatoriesModal
      :is-open="isFolioSignatoriesModalOpen"
      :submitters="template.submitters"
      @close="isFolioSignatoriesModalOpen = false"
      @save-signatories="onSaveSignatories"
    />
    <UnmappedSignatoriesModal
      :is-open="isUnmappedSignatoriesModalOpen"
      :submitters="template.submitters"
      :fields="template.fields"
      @close="isUnmappedSignatoriesModalOpen = false"
      @continue-mapping="onContinueMapping"
      @remove-unmapped="onRemoveUnmappedSignatories"
    />
  </div>
</template>

<script>
import Upload from './upload'
import Dropzone from './dropzone'
import HoverDropzone from './hover_dropzone'
import DragPlaceholder from './drag_placeholder'
import Fields from './fields'
import Document from './document'
import Logo from './logo'
import Contenteditable from './contenteditable'
import DocumentPreview from './preview'
import FieldSubmitter from './field_submitter'
import FieldType from './field_type'
import ManageParticipantsModal from './manage_participants_modal.vue'
import FolioSignatoriesModal from './folio_signatories_modal.vue'
import UnmappedSignatoriesModal from './unmapped_signatories_modal.vue'
import { IconPlus, IconUsersPlus, IconDeviceFloppy, IconChevronDown, IconEye, IconWritingSign, IconInnerShadowTop, IconInfoCircle, IconSend, IconArrowUp, IconListNumbers, IconFiles, IconX, IconPencil, IconMinus } from '@tabler/icons-vue'
import { v4 } from 'uuid'
import { ref, computed, toRaw } from 'vue'
import * as i18n from './i18n'

export default {
  name: 'TemplateBuilder',
  components: {
    Upload,
    DragPlaceholder,
    Document,
    Fields,
    IconInfoCircle,
    IconPlus,
    IconArrowUp,
    IconWritingSign,
    IconSend,
    Logo,
    Dropzone,
    HoverDropzone,
    DocumentPreview,
    IconInnerShadowTop,
    Contenteditable,
    IconUsersPlus,
    IconChevronDown,
    IconEye,
    IconDeviceFloppy,
    IconListNumbers,
    IconFiles,
    IconX,
    IconPencil,
    IconMinus,
    ManageParticipantsModal,
    FolioSignatoriesModal,
    UnmappedSignatoriesModal
  },
  provide () {
    return {
      template: this.template,
      save: this.save,
      t: this.t,
      assignDropAreaSize: this.assignDropAreaSize,
      currencies: this.currencies,
      locale: this.locale,
      baseFetch: this.baseFetch,
      fieldTypes: this.fieldTypes,
      backgroundColor: this.backgroundColor,
      withPhone: this.withPhone,
      withVerification: this.withVerification,
      withKba: this.withKba,
      withPayment: this.withPayment,
      isPaymentConnected: this.isPaymentConnected,
      withFormula: this.withFormula,
      withConditions: this.withConditions,
      isInlineSize: this.isInlineSize,
      defaultDrawFieldType: this.defaultDrawFieldType,
      selectedAreasRef: computed(() => this.selectedAreasRef),
      fieldsDragFieldRef: computed(() => this.fieldsDragFieldRef),
      isSelectModeRef: computed(() => this.isSelectModeRef),
      isCmdKeyRef: computed(() => this.isCmdKeyRef)
    }
  },
  props: {
    template: {
      type: Object,
      required: true
    },
    i18n: {
      type: Object,
      required: false,
      default: () => ({})
    },
    programmes: {
      type: Array,
      required: false,
      default: () => []
    },
    cohortId: {
      type: Number,
      required: false,
      default: null
    },
    programmeId: {
      type: Number,
      required: false,
      default: null
    },
    withFieldPlaceholder: {
      type: Boolean,
      required: false,
      default: false
    },
    withSignatureId: {
      type: Boolean,
      required: false,
      default: null
    },
    withDownload: {
      type: Boolean,
      required: false,
      default: false
    },
    withPreferences: {
      type: Boolean,
      required: false,
      default: true
    },
    backgroundColor: {
      type: String,
      required: false,
      default: ''
    },
    locale: {
      type: String,
      required: false,
      default: ''
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    withSendButton: {
      type: Boolean,
      required: false,
      default: true
    },
    withSignYourselfButton: {
      type: Boolean,
      required: false,
      default: true
    },
    signYourselfText: {
      type: String,
      required: false,
      default: null
    },
    inputMode: {
      type: Boolean,
      required: false,
      default: false
    },
    withHelp: {
      type: Boolean,
      required: false,
      default: true
    },
    withFieldsDetection: {
      type: Boolean,
      required: false,
      default: false
    },
    withAddPageButton: {
      type: Boolean,
      required: false,
      default: false
    },
    autosave: {
      type: Boolean,
      required: false,
      default: true
    },
    defaultFields: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultRequiredFields: {
      type: Array,
      required: false,
      default: () => []
    },
    withSelectedFieldType: {
      type: Boolean,
      required: false,
      default: true
    },
    defaultDrawFieldType: {
      type: String,
      required: false,
      default: 'text'
    },
    currencies: {
      type: Array,
      required: false,
      default: () => []
    },
    fieldTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultSubmitters: {
      type: Array,
      required: false,
      default: () => []
    },
    defineSubmitters: {
      type: Array,
      required: false,
      default: () => []
    },
    acceptFileTypes: {
      type: String,
      required: false,
      default: 'image/*, application/pdf, application/zip'
    },
    baseUrl: {
      type: String,
      required: false,
      default: ''
    },
    withLogo: {
      type: Boolean,
      required: false,
      default: true
    },
    onUpload: {
      type: Function,
      required: false,
      default () {
        return () => {}
      }
    },
    onSave: {
      type: Function,
      required: false,
      default () {
        return () => {}
      }
    },
    onChange: {
      type: Function,
      required: false,
      default () {
        return () => {}
      }
    },
    withStickySubmitters: {
      type: Boolean,
      required: false,
      default: true
    },
    withUploadButton: {
      type: Boolean,
      required: false,
      default: true
    },
    withTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    withFieldsSearch: {
      type: Boolean,
      required: false,
      default: null
    },
    withFieldsList: {
      type: Boolean,
      required: false,
      default: true
    },
    authenticityToken: {
      type: String,
      required: false,
      default: ''
    },
    withDocumentsList: {
      type: Boolean,
      required: false,
      default: true
    },
    withReplaceAndCloneUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    withPhone: {
      type: Boolean,
      required: false,
      default: false
    },
    withVerification: {
      type: Boolean,
      required: false,
      default: null
    },
    withKba: {
      type: Boolean,
      required: false,
      default: null
    },
    withPayment: {
      type: Boolean,
      required: false,
      default: false
    },
    isPaymentConnected: {
      type: Boolean,
      required: false,
      default: false
    },
    withFormula: {
      type: Boolean,
      required: false,
      default: false
    },
    withConditions: {
      type: Boolean,
      required: false,
      default: false
    },
    withGoogleDrive: {
      type: Boolean,
      required: false,
      default: false
    },
    onlyDefinedFields: {
      type: Boolean,
      required: false,
      default: false
    },
    fetchOptions: {
      type: Object,
      required: false,
      default: () => ({ headers: {} })
    },
    showTourStartForm: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      documentRefs: [],
      isBreakpointLg: false,
      isLeftDrawerOpen: false,
      isRightDrawerOpen: false,
      isDownloading: false,
      showScrollTopFab: false,
      isLoadingBlankPage: false,
      isSaving: false,
      isDetectingPageFields: false,
      detectingAnalyzingProgress: null,
      detectingFieldsAddedCount: null,
      selectedSubmitter: null,
      showDrawField: false,
      pendingFieldAttachmentUuids: [],
      drawField: null,
      drawFieldType: null,
      drawOption: null,
      dragField: null,
      isDragFile: false,
      participantRoleToManage: 'Learner',
      isFolioSignatoriesModalOpen: false,
      isUnmappedSignatoriesModalOpen: false,
      isSaveDropdownOpen: false,
      isDirty: false,
      zoomLevel: 100
    }
  },
  computed: {
    documentsZoomStyle () {
      if (this.zoomLevel === 100) return {}
      return {
        width: `${this.zoomLevel}%`,
        minWidth: `${this.zoomLevel}%`,
        transition: 'width 0.15s ease-out'
      }
    },
    submitterDefaultNames: FieldSubmitter.computed.names,
    isSelectModeRef: () => ref(false),
    isCmdKeyRef: () => ref(false),
    fieldsDragFieldRef: () => ref(),
    selectedAreasRef: () => ref([]),
    isSequentialSigning () {
      return this.template.preferences?.submitters_order !== 'random'
    },
    isCohortProgramme () {
      return Boolean(this.cohortId || this.programmeId)
    },
    saveAndPreviewHref () {
      if (this.isCohortProgramme) {
        return `/cohorts/${this.cohortId}/programmes/${this.programmeId}`
      }
      return this.template.is_reusable ? `/templates/${this.template.id}/form` : `/documents/${this.template.id}/form`
    },
    currentProgrammeName () {
      const prog = this.programmes.find(p => p.id === this.programmeId)
      const name = prog ? prog.name : ''
      return this.truncate(name, 20)
    },
    language () {
      return this.locale.split('-')[0].toLowerCase()
    },
    withPrefillable () {
      if (this.template.fields) {
        return this.template.fields.some((f) => f.prefillable)
      } else {
        return false
      }
    },
    isInlineSize () {
      return CSS.supports('container-type: size')
    },
    lowestSelectedArea () {
      return this.selectedAreasRef.value.reduce((acc, area) => {
        return area.y + area.h < acc.y + acc.h ? acc : area
      }, this.selectedAreasRef.value[0])
    },
    lastSelectedArea () {
      return this.selectedAreasRef.value[this.selectedAreasRef.value.length - 1]
    },
    fieldNames: FieldType.computed.fieldNames,
    isDrawingActive () {
      return !!(this.drawField || this.showDrawField)
    },
    currentDrawFieldName () {
      if (this.drawField?.name) return this.drawField.name
      const type = this.drawField?.type || this.drawFieldType
      if (type && this.fieldNames?.[type]) return this.fieldNames[type]
      return type || 'Field'
    },
    isMobile () {
      if (this.isBreakpointLg) {
        return true
      }
      const isMobileSafariIos = 'ontouchstart' in window && navigator.maxTouchPoints > 0 && /AppleWebKit/i.test(navigator.userAgent)

      return isMobileSafariIos || /android|iphone|ipad/i.test(navigator.userAgent)
    },
    defaultDateFormat () {
      const isUsBrowser = Intl.DateTimeFormat().resolvedOptions().locale.endsWith('-US')
      const isUsTimezone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(new Date()).match(/\s(?:CST|CDT|PST|PDT|EST|EDT)$/)

      return this.localeDateFormats[this.locale] || ((isUsBrowser || isUsTimezone) ? 'MM/DD/YYYY' : 'DD/MM/YYYY')
    },
    localeDateFormats () {
      return {
        'de-DE': 'DD.MM.YYYY',
        'fr-FR': 'DD/MM/YYYY',
        'it-IT': 'DD/MM/YYYY',
        'en-GB': 'DD/MM/YYYY',
        'es-ES': 'DD/MM/YYYY'
      }
    },
    fieldAreasIndex () {
      const areas = {}

      this.template.fields.forEach((f) => {
        (f.areas || []).forEach((a) => {
          areas[a.attachment_uuid] ||= {}

          const acc = (areas[a.attachment_uuid][a.page] ||= [])

          acc.push({ area: a, field: f })
        })
      })

      return areas
    },
    isAllRequiredFieldsAdded () {
      return !this.defaultRequiredFields?.some((f) => {
        return !this.template.fields?.some((field) => field.name === f.name)
      })
    },
    selectedField () {
      return this.template.fields.find((f) => f.areas?.includes(this.lastSelectedArea))
    },
    sortedDocuments () {
      return this.template.schema.map((item) => {
        return this.template.documents.find(doc => doc.uuid === item.attachment_uuid)
      })
    }
  },
  watch: {
    template: {
      deep: true,
      handler () {
        if (!this.initialTemplateSnapshot) return
        this.isDirty = this.templateSnapshot() !== this.initialTemplateSnapshot
      }
    }
  },
  created () {
    if (!this.template.fields?.length && this.template.submitters?.length === 1) {
      if (this.template.submitters[0]?.name === 'First Party') {
        this.template.submitters[0].name = this.t('first_party')
      }
    }

    const existingSubmittersUuids = this.defaultSubmitters.map((name) => {
      return this.template.submitters.find(e => e.name === name)?.uuid
    })

    this.defaultSubmitters.forEach((name, index) => {
      const submitter = (this.template.submitters[index] ||= {})

      submitter.name = name

      if (existingSubmittersUuids.filter(Boolean).length) {
        submitter.uuid = existingSubmittersUuids[index] || submitter.uuid || v4()
      } else {
        submitter.uuid ||= v4()
      }
    })

    const defineSubmittersUuids = this.defineSubmitters.map((name) => {
      return this.template.submitters.find(e => e.name === name)?.uuid
    })

    this.defineSubmitters.forEach((name, index) => {
      const submitter = (this.template.submitters[index] ||= {})

      submitter.name = name || this.submitterDefaultNames[index]

      if (defineSubmittersUuids.filter(Boolean).length || existingSubmittersUuids.filter(Boolean).length) {
        submitter.uuid = defineSubmittersUuids[index] || existingSubmittersUuids[index] || submitter.uuid || v4()
      } else {
        submitter.uuid ||= v4()
      }
    })

    this.selectedSubmitter = this.template.submitters[0]
  },
  mounted () {
    this.undoStack = [JSON.stringify(this.template)]
    this.redoStack = []

    this.$nextTick(() => {
      this.initialTemplateSnapshot = this.templateSnapshot()
      this.isDirty = false
      this.onWindowResize()
    })

    document.addEventListener('keyup', this.onKeyUp)
    document.addEventListener('click', this.onDocumentClick)
    window.addEventListener('keydown', this.onKeyDown)

    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('dragleave', this.onWindowDragLeave)

    this.$nextTick(() => {
      if (document.location.search?.includes('stripe_connect_success')) {
        document.querySelector('form[action="/auth/stripe_connect"]')?.closest('.dropdown')?.querySelector('label')?.focus()
      }
    })

    this.template.schema.forEach((item) => {
      if (item.pending_fields) {
        this.pendingFieldAttachmentUuids.push(item.attachment_uuid)
      }
    })
  },
  unmounted () {
    document.removeEventListener('keyup', this.onKeyUp)
    document.removeEventListener('click', this.onDocumentClick)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('dragleave', this.onWindowDragLeave)
  },
  beforeUpdate () {
    this.documentRefs = []
  },
  methods: {
    templateSnapshot () {
      return JSON.stringify({
        name: this.template.name,
        schema: this.template.schema,
        submitters: this.template.submitters,
        fields: this.template.fields,
        preferences: this.template.preferences
      })
    },
    onToggleSequentialSigning (checked) {
      if (!this.template.preferences) {
        this.template.preferences = {}
      }
      this.template.preferences.submitters_order = checked ? 'preserved' : 'random'
      this.isDirty = this.templateSnapshot() !== this.initialTemplateSnapshot
    },
    onSaveAndPreviewClick (e) {
      this.closeDropdown()
      const url = this.saveAndPreviewHref
      if (this.isDirty) {
        e.preventDefault()
        this.save({ force: true }).then(() => {
          window.Turbo.visit(url)
        })
      }
    },
    handleEditRole (roleOrName) {
      if (!this.isCohortProgramme) return

      this.participantRoleToManage = typeof roleOrName === 'string' ? roleOrName : roleOrName.name
      this.$nextTick(() => {
        if (this.$refs.participantsModal) {
          this.$refs.participantsModal.openModal()
        }
      })
    },
    onParticipantsSaved () {
      // Just console for now, the modal handles its own state
      console.log('Participants saved successfully.')
    },
    truncate (text, length) {
      if (!text) return ''
      if (text.length <= length) return text
      return text.substring(0, length) + '...'
    },
    onPagesScroll (e) {
      // Show FAB after roughly 2 pages of scrolling (assuming ~800px per page)
      this.showScrollTopFab = e.target.scrollTop > 1600
    },
    scrollToTop () {
      const container = document.getElementById('pages_container')
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    toRaw,
    toggleSelectMode () {
      this.isSelectModeRef.value = !this.isSelectModeRef.value

      if (!this.isSelectModeRef.value && this.selectedAreasRef.value.length > 1) {
        this.selectedAreasRef.value = []
      }
    },
    deleteSelectedAreas () {
      [...this.selectedAreasRef.value].forEach((area) => {
        this.removeArea(area, false)
      })

      this.save()
    },
    moveSelectedAreas (dx, dy) {
      let clampedDx = dx
      let clampedDy = dy

      const rectIndex = {}

      this.selectedAreasRef.value.map((area) => {
        const key = `${area.attachment_uuid}-${area.page}`

        let rect = rectIndex[key]

        if (!rect) {
          const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
          const page = documentRef.pageRefs[area.page].$refs.image
          rect = page.getBoundingClientRect()

          rectIndex[key] = rect
        }

        const normalizedDx = dx / rect.width
        const normalizedDy = dy / rect.height

        const maxDxLeft = -area.x
        const maxDxRight = 1 - area.w - area.x
        const maxDyTop = -area.y
        const maxDyBottom = 1 - area.h - area.y

        if (normalizedDx < maxDxLeft) clampedDx = Math.max(clampedDx, maxDxLeft * rect.width)
        if (normalizedDx > maxDxRight) clampedDx = Math.min(clampedDx, maxDxRight * rect.width)
        if (normalizedDy < maxDyTop) clampedDy = Math.max(clampedDy, maxDyTop * rect.height)
        if (normalizedDy > maxDyBottom) clampedDy = Math.min(clampedDy, maxDyBottom * rect.height)

        return [area, rect]
      }).forEach(([area, rect]) => {
        area.x += clampedDx / rect.width
        area.y += clampedDy / rect.height
      })

      this.debouncedSave()
    },
    alignSelectedAreas (direction) {
      const areas = this.selectedAreasRef.value

      let targetValue

      if (direction === 'left') {
        targetValue = Math.min(...areas.map(a => a.x))
        areas.forEach((area) => { area.x = targetValue })
      } else if (direction === 'right') {
        targetValue = Math.max(...areas.map(a => a.x + a.w))
        areas.forEach((area) => { area.x = targetValue - area.w })
      } else if (direction === 'top') {
        targetValue = Math.min(...areas.map(a => a.y))
        areas.forEach((area) => { area.y = targetValue })
      } else if (direction === 'bottom') {
        targetValue = Math.max(...areas.map(a => a.y + a.h))
        areas.forEach((area) => { area.y = targetValue - area.h })
      }

      this.save()
    },
    download () {
      this.isDownloading = true

      this.baseFetch(`/templates/${this.template.id}/documents`).then(async (response) => {
        if (response.ok) {
          const urls = await response.json()
          const isMobileSafariIos = 'ontouchstart' in window && navigator.maxTouchPoints > 0 && /AppleWebKit/i.test(navigator.userAgent)
          const isSafariIos = isMobileSafariIos || /iPhone|iPad|iPod/i.test(navigator.userAgent)

          if (isSafariIos && urls.length > 1) {
            this.downloadSafariIos(urls)
          } else {
            this.downloadUrls(urls)
          }
        } else {
          alert(this.t('failed_to_download_files'))
        }
      })
    },
    downloadUrls (urls) {
      const fileRequests = urls.map((url) => {
        return () => {
          return fetch(url).then(async (resp) => {
            const blobUrl = URL.createObjectURL(await resp.blob())
            const link = document.createElement('a')

            link.href = blobUrl
            link.setAttribute('download', decodeURI(url.split('/').pop()))

            link.click()

            URL.revokeObjectURL(blobUrl)
          })
        }
      })

      fileRequests.reduce(
        (prevPromise, request) => prevPromise.then(() => request()),
        Promise.resolve()
      ).finally(() => {
        this.isDownloading = false
      })
    },
    downloadSafariIos (urls) {
      const fileRequests = urls.map((url) => {
        return fetch(url).then(async (resp) => {
          const blob = await resp.blob()
          const blobUrl = URL.createObjectURL(blob.slice(0, blob.size, 'application/octet-stream'))
          const link = document.createElement('a')

          link.href = blobUrl
          link.setAttribute('download', decodeURI(url.split('/').pop()))

          return link
        })
      })

      Promise.all(fileRequests).then((links) => {
        links.forEach((link, index) => {
          setTimeout(() => {
            link.click()

            URL.revokeObjectURL(link.href)
          }, index * 50)
        })
      }).finally(() => {
        this.isDownloading = false
      })
    },
    onDragover (e) {
      if (this.$refs.dragPlaceholder?.dragPlaceholder) {
        this.$refs.dragPlaceholder.isMask = e.target.id === 'mask'

        const ref = this.$refs.dragPlaceholder.dragPlaceholder

        ref.x = e.clientX - ref.offsetX
        ref.y = e.clientY - ref.offsetY
      } else if (e.dataTransfer?.types?.includes('Files')) {
        this.isDragFile = true
      }
    },
    onWindowDragLeave (event) {
      if (event.clientX <= 0 || event.clientY <= 0 || event.clientX >= window.innerWidth || event.clientY >= window.innerHeight) {
        this.isDragFile = false
      }
    },
    reorderFields (item) {
      const itemFields = []
      const fields = []
      const fieldAreasIndex = {}

      const attachmentUuids = this.template.schema.map((e) => e.attachment_uuid)

      this.template.fields.forEach((f) => {
        if (f.areas?.length) {
          const firstArea = f.areas.reduce((min, a) => {
            return attachmentUuids.indexOf(a.attachment_uuid) < attachmentUuids.indexOf(min.attachment_uuid) ? a : min
          }, f.areas[0])

          if (firstArea.attachment_uuid === item.attachment_uuid) {
            itemFields.push(f)
          } else {
            fields.push(f)
          }
        } else {
          fields.push(f)
        }
      })

      const sortArea = (aArea, bArea) => {
        if (aArea.attachment_uuid === bArea.attachment_uuid) {
          if (aArea.page === bArea.page) {
            const aY = aArea.y + aArea.h
            const bY = bArea.y + bArea.h

            if (Math.abs(aY - bY) < 0.01 || (aArea.h < bArea.h ? (aArea.y >= bArea.y && aY <= bY) : (bArea.y >= aArea.y && bY <= aY))) {
              if (aArea.x === bArea.x) {
                return 0
              } else {
                return aArea.x - bArea.x
              }
            } else {
              return (aArea.y + aArea.h) - (bArea.y + bArea.h)
            }
          } else {
            return aArea.page - bArea.page
          }
        } else {
          return attachmentUuids.indexOf(aArea.attachment_uuid) - attachmentUuids.indexOf(bArea.attachment_uuid)
        }
      }

      itemFields.sort((aField, bField) => {
        const aArea = (fieldAreasIndex[aField.uuid] ||= [...(aField.areas || [])].sort(sortArea)[0])
        const bArea = (fieldAreasIndex[bField.uuid] ||= [...(bField.areas || [])].sort(sortArea)[0])

        return sortArea(aArea, bArea)
      })

      const insertBeforeAttachmentUuids = attachmentUuids.slice(this.template.schema.indexOf(item) + 1)

      let sortedFields = []

      if (insertBeforeAttachmentUuids.length) {
        const insertAfterField = fields.find((f) => {
          if (f.areas?.length) {
            return f.areas.find((a) => insertBeforeAttachmentUuids.includes(a.attachment_uuid))
          } else {
            return false
          }
        })

        if (insertAfterField) {
          fields.splice(fields.indexOf(insertAfterField), 0, ...itemFields)

          sortedFields = fields
        } else {
          sortedFields = fields.concat(itemFields)
        }
      } else {
        if (fields.length && itemFields.length && this.template.fields.indexOf(fields[0]) > this.template.fields.indexOf(itemFields[0])) {
          sortedFields = itemFields.concat(fields)
        } else {
          sortedFields = fields.concat(itemFields)
        }
      }

      if (this.template.fields.length === sortedFields.length) {
        this.template.fields = sortedFields
        this.save()
      }
    },
    findFieldInsertIndex (field) {
      if (!field.areas?.length) return -1

      const area = field.areas[0]

      const attachmentUuidsIndex = this.template.schema.reduce((acc, e, index) => {
        acc[e.attachment_uuid] = index

        return acc
      }, {})

      const compareAreas = (a, b) => {
        const aAttIdx = attachmentUuidsIndex[a.attachment_uuid]
        const bAttIdx = attachmentUuidsIndex[b.attachment_uuid]

        if (aAttIdx !== bAttIdx) return aAttIdx - bAttIdx
        if (a.page !== b.page) return a.page - b.page

        const aY = a.y + a.h
        const bY = b.y + b.h

        if (Math.abs(aY - bY) < 0.01) return a.x - b.x
        if (a.h < b.h ? a.y >= b.y && aY <= bY : b.y >= a.y && bY <= aY) return a.x - b.x

        return aY - bY
      }

      let closestBeforeIndex = -1
      let closestBeforeArea = null
      let closestAfterIndex = -1
      let closestAfterArea = null

      this.template.fields.forEach((f, index) => {
        if (f.submitter_uuid === field.submitter_uuid) {
          (f.areas || []).forEach((a) => {
            const cmp = compareAreas(a, area)

            if (cmp < 0) {
              if (!closestBeforeArea || (compareAreas(a, closestBeforeArea) > 0 && closestBeforeIndex < index)) {
                closestBeforeIndex = index
                closestBeforeArea = a
              }
            } else {
              if (!closestAfterArea || (compareAreas(a, closestAfterArea) < 0 && closestAfterIndex > index)) {
                closestAfterIndex = index
                closestAfterArea = a
              }
            }
          })
        }
      })

      if (closestBeforeIndex !== -1) return closestBeforeIndex + 1
      if (closestAfterIndex !== -1) return closestAfterIndex

      return -1
    },
    insertField (field) {
      const insertIndex = this.findFieldInsertIndex(field)

      if (insertIndex !== -1) {
        this.template.fields.splice(insertIndex, 0, field)
      } else {
        this.template.fields.push(field)
      }
    },
    closeDropdown () {
      this.isSaveDropdownOpen = false
      document.activeElement.blur()
    },
    onDocumentClick (e) {
      if (this.isSaveDropdownOpen && !e.target.closest('#save_button_container')) {
        this.isSaveDropdownOpen = false
        if (document.activeElement && document.activeElement.closest('#save_button_container')) {
          document.activeElement.blur()
        }
      }
    },
    t (key) {
      return this.i18n[key] || i18n[this.language]?.[key] || i18n.en[key] || key
    },
    removePendingFields () {
      this.template.fields = this.template.fields.filter((f) => {
        return this.template.schema.find((item) => item.attachment_uuid === f.attachment_uuid && item.pending_fields)
      })

      this.save()
    },
    addField (type, area = null) {
      if (this.isCohortProgramme && type === 'file' && this.selectedSubmitter?.uuid !== 'student') {
        alert('File attachments are only applicable to learners (Student role) in cohort programmes.')
        return
      }

      const field = {
        name: '',
        uuid: v4(),
        required: type !== 'checkbox',
        areas: area ? [area] : [],
        submitter_uuid: this.selectedSubmitter.uuid,
        type
      }

      if (['select', 'multiple', 'radio'].includes(type)) {
        field.options = [{ value: '', uuid: v4() }, { value: '', uuid: v4() }]
      }

      if (type === 'stamp') {
        field.readonly = true
      }

      if (type === 'datenow') {
        field.type = 'date'
        field.readonly = true
        field.default_value = '{{date}}'
      }

      if (type === 'date') {
        field.preferences = {
          format: this.defaultDateFormat
        }
      }

      if (field.type === 'strikethrough') {
        field.readonly = true
        field.default_value = true
      }

      if (type === 'signature' && [true, false].includes(this.withSignatureId)) {
        field.preferences ||= {}
        field.preferences.with_signature_id = this.withSignatureId
      }

      this.insertField(field)

      this.save()
    },
    startFieldDraw ({ name, type }) {
      const existingField = this.template.fields?.find((f) => f.submitter_uuid === this.selectedSubmitter.uuid && name && name === f.name)

      if (existingField) {
        this.drawField = existingField
      } else {
        const field = {
          name: name || '',
          uuid: v4(),
          required: type !== 'checkbox',
          areas: [],
          submitter_uuid: this.selectedSubmitter.uuid,
          type
        }

        if (['select', 'multiple', 'radio'].includes(type)) {
          field.options = [{ value: '', uuid: v4() }, { value: '', uuid: v4() }]
        }

        if (type === 'stamp') {
          field.readonly = true
        }

        if (type === 'date') {
          field.preferences = {
            format: this.defaultDateFormat
          }
        }

        this.drawField = field
      }

      this.drawOption = null
    },
    undo () {
      if (this.undoStack.length > 1) {
        this.undoStack.pop()
        const stringData = this.undoStack[this.undoStack.length - 1]
        const currentStringData = JSON.stringify(this.template)

        if (stringData && stringData !== currentStringData) {
          this.redoStack.push(currentStringData)

          Object.assign(this.template, JSON.parse(stringData))

          this.save()
        }
      }
    },
    redo () {
      const stringData = this.redoStack.pop()
      this.lastRedoData = stringData
      const currentStringData = JSON.stringify(this.template)

      if (stringData && stringData !== currentStringData) {
        if (this.undoStack[this.undoStack.length - 1] !== currentStringData) {
          this.undoStack.push(currentStringData)
        }

        Object.assign(this.template, JSON.parse(stringData))

        this.save()
      }
    },
    onWindowResize (e) {
      const breakpointLg = 1024

      this.isBreakpointLg = window.innerWidth < breakpointLg
      if (!this.isBreakpointLg) {
        this.isLeftDrawerOpen = false
        this.isRightDrawerOpen = false
      }
    },
    triggerMobileSelfSign () {
      if (this.template.submitters.length < 2) {
        if (this.$refs.selfSignForm) {
          this.$refs.selfSignForm.submit()
        }
      } else {
        this.onSignFolioClick()
      }
    },
    setDocumentRefs (el) {
      if (el) {
        this.documentRefs.push(el)
      }
    },
    scrollIntoDocument (item) {
      const ref = this.documentRefs.find((e) => e.document.uuid === item.attachment_uuid)

      ref.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    clearDrawField () {
      this.drawField = null
      this.drawOption = null
      this.showDrawField = false

      if (!this.withSelectedFieldType) {
        this.drawFieldType = ''
      }
    },
    cancelMobileDraw () {
      this.drawField = null
      this.drawOption = null
      this.showDrawField = false
      this.drawFieldType = ''
    },
    zoomIn () {
      if (this.zoomLevel < 200) {
        this.zoomLevel = Math.min(200, this.zoomLevel + 25)
      }
    },
    zoomOut () {
      if (this.zoomLevel > 50) {
        this.zoomLevel = Math.max(50, this.zoomLevel - 25)
      }
    },
    resetZoom () {
      this.zoomLevel = 100
    },
    getCurrentlyVisiblePageInfo () {
      const defaultDocUuid = this.sortedDocuments[0]?.uuid || ''
      const container = document.getElementById('pages_container')
      if (!container) return { documentUuid: defaultDocUuid, pageIndex: 0 }

      const containerRect = container.getBoundingClientRect()
      const centerY = containerRect.top + (containerRect.height / 3)

      const pageEls = container.querySelectorAll('[data-page]')
      for (const el of pageEls) {
        const rect = el.getBoundingClientRect()
        if (rect.top <= centerY && rect.bottom >= centerY) {
          const pageIndex = parseInt(el.getAttribute('data-page') || '0', 10)
          const docUuid = el.closest('[data-document-uuid]')?.getAttribute('data-document-uuid') || defaultDocUuid
          return { documentUuid: docUuid, pageIndex }
        }
      }
      return { documentUuid: defaultDocUuid, pageIndex: 0 }
    },
    addDirectlyOnCurrentPage (type) {
      const fieldType = type || this.drawFieldType || 'text'
      const { documentUuid, pageIndex } = this.getCurrentlyVisiblePageInfo()

      const area = {
        x: 0.25,
        y: 0.35,
        w: 0.35,
        h: 0.05,
        page: pageIndex,
        attachment_uuid: documentUuid
      }

      if (['checkbox', 'radio', 'multiple'].includes(fieldType)) {
        area.w = 0.06
        area.h = 0.04
      } else if (fieldType === 'signature' || fieldType === 'initials') {
        area.w = 0.35
        area.h = 0.08
      } else if (fieldType === 'date' || fieldType === 'number') {
        area.w = 0.30
        area.h = 0.05
      }

      if (this.drawField) {
        this.drawField.areas ||= []
        this.drawField.areas.push(area)
        if (this.template.fields.indexOf(this.drawField) === -1) {
          this.insertField(this.drawField)
        }
        this.selectedAreasRef.value = [area]
      } else {
        this.addField(fieldType, area)
        this.selectedAreasRef.value = [area]
      }

      this.save()
      this.cancelMobileDraw()
    },
    onKeyUp (e) {
      this.isCmdKeyRef.value = false

      if (e.code === 'Escape') {
        this.selectedAreasRef.value = []
        this.clearDrawField()
      }

      if (this.editable && ['Backspace', 'Delete'].includes(e.key) && document.activeElement === document.body) {
        if (this.selectedAreasRef.value.length > 1) {
          this.deleteSelectedAreas()
        } else if (this.selectedAreasRef.value.length) {
          this.removeArea(this.lastSelectedArea)
        }
      }
    },
    onKeyDown (event) {
      if (event.key === 'Tab' && document.activeElement === document.body) {
        event.stopImmediatePropagation()
        event.preventDefault()

        this.toggleSelectMode()
      } else if ((event.metaKey && event.shiftKey && event.key === 'z') || (event.ctrlKey && event.key === 'Z')) {
        event.stopImmediatePropagation()
        event.preventDefault()

        this.selectedAreasRef.value = []

        this.redo()
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.stopImmediatePropagation()
        event.preventDefault()

        this.selectedAreasRef.value = []

        this.undo()
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'c' && document.activeElement === document.body) {
        if (this.selectedAreasRef.value.length > 1) {
          event.preventDefault()
          this.copySelectedAreas()
        } else if (this.selectedAreasRef.value.length) {
          event.preventDefault()
          this.copyField()
        }
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'v' && this.hasClipboardData() && document.activeElement === document.body) {
        event.preventDefault()

        this.pasteField()
      } else if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key) && document.activeElement === document.body) {
        if (this.selectedAreasRef.value.length > 1) {
          event.preventDefault()
          this.handleSelectedAreasArrows(event)
        } else if (this.selectedAreasRef.value.length) {
          event.preventDefault()
          this.handleAreaArrows(event)
        }
      } else if (event.metaKey || event.ctrlKey) {
        this.isCmdKeyRef.value = true
      }
    },
    handleSelectedAreasArrows (event) {
      if (!this.editable) {
        return
      }

      const diff = (event.shiftKey ? 5.0 : 1.0)
      let dx = 0
      let dy = 0

      if (event.key === 'ArrowRight') {
        dx = diff
      } else if (event.key === 'ArrowLeft') {
        dx = -diff
      } else if (event.key === 'ArrowUp') {
        dy = -diff
      } else if (event.key === 'ArrowDown') {
        dy = diff
      }

      this.moveSelectedAreas(dx, dy)
    },
    handleAreaArrows (event) {
      if (!this.editable) {
        return
      }

      const area = this.lastSelectedArea
      const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
      const page = documentRef.pageRefs[area.page].$refs.image
      const rect = page.getBoundingClientRect()
      const diff = (event.shiftKey ? 5.0 : 1.0)

      if (event.key === 'ArrowRight' && event.altKey) {
        area.w = Math.min(Math.max(area.w + diff / rect.width, 0), 1 - area.x)
      } else if (event.key === 'ArrowLeft' && event.altKey) {
        area.w = Math.min(Math.max(area.w - diff / rect.width, 0), 1 - area.x)
      } else if (event.key === 'ArrowUp' && event.altKey) {
        area.h = Math.min(Math.max(area.h - diff / rect.height, 0), 1 - area.y)
      } else if (event.key === 'ArrowDown' && event.altKey) {
        area.h = Math.min(Math.max(area.h + diff / rect.height, 0), 1 - area.y)
      } else if (event.key === 'ArrowRight') {
        area.x = Math.min(Math.max(area.x + diff / rect.width, 0), 1 - area.w)
      } else if (event.key === 'ArrowLeft') {
        area.x = Math.min(Math.max(area.x - diff / rect.width, 0), 1 - area.w)
      } else if (event.key === 'ArrowUp') {
        area.y = Math.min(Math.max(area.y - diff / rect.height, 0), 1 - area.h)
      } else if (event.key === 'ArrowDown') {
        area.y = Math.min(Math.max(area.y + diff / rect.height, 0), 1 - area.h)
      }

      this.debouncedSave()
    },
    debouncedSave () {
      clearTimeout(this._saveTimeout)

      this._saveTimeout = setTimeout(() => {
        this.save()
      }, 700)
    },
    removeArea (area, save = true) {
      const field = this.template.fields.find((f) => f.areas?.includes(area))

      field.areas.splice(field.areas.indexOf(area), 1)

      if (!field.areas.length) {
        this.template.fields.splice(this.template.fields.indexOf(field), 1)

        this.removeFieldConditions(field)
      }

      this.selectedAreasRef.value.splice(this.selectedAreasRef.value.indexOf(area), 1)

      if (save) {
        this.save()
      }
    },
    removeFieldConditions (field) {
      this.template.fields.forEach((f) => {
        if (f.conditions) {
          f.conditions.forEach((c) => {
            if (c.field_uuid === field.uuid) {
              f.conditions.splice(f.conditions.indexOf(c), 1)
            }
          })
        }
      })

      this.template.schema.forEach((item) => {
        if (item.conditions) {
          item.conditions.forEach((c) => {
            if (c.field_uuid === field.uuid) {
              item.conditions.splice(item.conditions.indexOf(c), 1)
            }
          })
        }
      })
    },
    copyField () {
      const area = this.lastSelectedArea

      if (!area) return

      const field = this.template.fields.find((f) => f.areas?.includes(area))

      if (!field) return

      const clipboardData = {
        field: JSON.parse(JSON.stringify(field)),
        area: JSON.parse(JSON.stringify(area)),
        templateId: this.template.id,
        timestamp: Date.now()
      }

      delete clipboardData.field.areas
      delete clipboardData.field.uuid
      delete clipboardData.field.submitter_uuid

      try {
        localStorage.setItem('docuseal_clipboard', JSON.stringify(clipboardData))
      } catch (e) {
        console.error('Failed to save clipboard:', e)
      }
    },
    copySelectedAreas () {
      const items = []

      const areas = this.selectedAreasRef.value

      const minX = Math.min(...areas.map(a => a.x))
      const minY = Math.min(...areas.map(a => a.y))

      areas.forEach((area) => {
        const field = this.template.fields.find((f) => f.areas?.includes(area))

        if (!field) return

        const fieldCopy = JSON.parse(JSON.stringify(field))
        const areaCopy = JSON.parse(JSON.stringify(area))

        delete fieldCopy.areas
        delete fieldCopy.submitter_uuid

        areaCopy.relativeX = area.x - minX
        areaCopy.relativeY = area.y - minY

        items.push({ field: fieldCopy, area: areaCopy })
      })

      const clipboardData = {
        items,
        templateId: this.template.id,
        timestamp: Date.now(),
        isGroup: true
      }

      try {
        localStorage.setItem('docuseal_clipboard', JSON.stringify(clipboardData))
      } catch (e) {
        console.error('Failed to save clipboard:', e)
      }
    },
    pasteField (targetPosition = null) {
      const clipboard = localStorage.getItem('docuseal_clipboard')

      if (!clipboard) return

      const data = JSON.parse(clipboard)

      if (Date.now() - data.timestamp >= 3600000) {
        localStorage.removeItem('docuseal_clipboard')

        return
      }

      if (data.isGroup && data.items?.length) {
        this.pasteFieldGroup(data, targetPosition)

        return
      }

      const field = data.field
      const area = data.area
      const isSameTemplate = data.templateId === this.template.id

      if (!field || !area) return

      if (!isSameTemplate) {
        delete field.conditions
        delete field.preferences?.formula
      }

      const defaultAttachmentUuid = this.template.schema[0]?.attachment_uuid

      if (field && (this.lowestSelectedArea || targetPosition)) {
        const attachmentUuid = targetPosition?.attachment_uuid ||
          (this.template.documents.find((d) => d.uuid === this.lowestSelectedArea.attachment_uuid) ? this.lowestSelectedArea.attachment_uuid : null) ||
          defaultAttachmentUuid

        const newArea = {
          ...JSON.parse(JSON.stringify(area)),
          attachment_uuid: attachmentUuid,
          page: targetPosition?.page ?? (attachmentUuid === this.lowestSelectedArea.attachment_uuid ? this.lowestSelectedArea.page : 0),
          x: targetPosition ? (targetPosition.x - area.w / 2) : Math.min(...this.selectedAreasRef.value.map((area) => area.x)),
          y: targetPosition ? (targetPosition.y - area.h / 2) : (this.lowestSelectedArea.y + this.lowestSelectedArea.h * 1.3)
        }

        const newField = {
          ...JSON.parse(JSON.stringify(field)),
          uuid: v4(),
          submitter_uuid: this.selectedSubmitter.uuid,
          areas: [newArea]
        }

        if (['radio', 'multiple'].includes(field.type) && field.options?.length) {
          const oldOptionUuid = area.option_uuid
          const optionsMap = {}

          newField.options = field.options.map((opt) => {
            const newUuid = v4()
            optionsMap[opt.uuid] = newUuid
            return { ...opt, uuid: newUuid }
          })

          newArea.option_uuid = optionsMap[oldOptionUuid] || newField.options[0].uuid
        }

        this.insertField(newField)

        this.selectedAreasRef.value = [newArea]

        this.save()
      }
    },
    pasteFieldGroup (data, targetPosition) {
      const isSameTemplate = data.templateId === this.template.id
      const defaultAttachmentUuid = this.template.schema[0]?.attachment_uuid

      const attachmentUuid = targetPosition?.attachment_uuid ||
        (this.lowestSelectedArea && this.template.documents.find((d) => d.uuid === this.lowestSelectedArea.attachment_uuid) ? this.lowestSelectedArea.attachment_uuid : null) ||
        defaultAttachmentUuid

      const page = targetPosition?.page ?? (this.lowestSelectedArea && attachmentUuid === this.lowestSelectedArea.attachment_uuid ? this.lowestSelectedArea.page : 0)

      let baseX, baseY

      if (targetPosition) {
        baseX = targetPosition.x
        baseY = targetPosition.y
      } else if (this.lowestSelectedArea) {
        baseX = Math.min(...this.selectedAreasRef.value.map((area) => area.x))
        baseY = this.lowestSelectedArea.y + this.lowestSelectedArea.h * 1.3
      } else {
        baseX = 0.1
        baseY = 0.1
      }

      const newAreas = []

      const fieldUuidIndex = {}
      const fieldOptionsMap = {}

      data.items.forEach((item) => {
        const field = JSON.parse(JSON.stringify(item.field))
        const area = JSON.parse(JSON.stringify(item.area))

        if (!isSameTemplate) {
          delete field.conditions
          delete field.preferences?.formula
        }

        const newArea = {
          ...area,
          attachment_uuid: attachmentUuid,
          page,
          x: baseX + (area.relativeX || 0),
          y: baseY + (area.relativeY || 0)
        }

        delete newArea.relativeX
        delete newArea.relativeY

        const newField = fieldUuidIndex[field.uuid] || {
          ...field,
          uuid: v4(),
          submitter_uuid: this.selectedSubmitter.uuid,
          areas: []
        }

        fieldUuidIndex[field.uuid] = newField

        newField.areas.push(newArea)
        newAreas.push(newArea)

        if (['radio', 'multiple'].includes(field.type) && field.options?.length) {
          const oldOptionUuid = area.option_uuid

          if (!fieldOptionsMap[field.uuid]) {
            fieldOptionsMap[field.uuid] = {}

            newField.options = field.options.map((opt) => {
              const newUuid = v4()

              fieldOptionsMap[field.uuid][opt.uuid] = newUuid

              return { ...opt, uuid: newUuid }
            })
          }

          newArea.option_uuid = fieldOptionsMap[field.uuid][oldOptionUuid] || newField.options[0].uuid
        }
      })

      Object.values(fieldUuidIndex).forEach((field) => {
        this.insertField(field)
      })

      this.selectedAreasRef.value = [...newAreas]

      this.save()
    },
    hasClipboardData () {
      try {
        const clipboard = localStorage.getItem('docuseal_clipboard')

        if (clipboard) {
          const data = JSON.parse(clipboard)

          return Date.now() - data.timestamp < 3600000
        }

        return false
      } catch {
        return false
      }
    },
    pushUndo () {
      const stringData = JSON.stringify(this.template)

      if (this.undoStack[this.undoStack.length - 1] !== stringData) {
        this.undoStack.push(stringData)

        if (this.lastRedoData !== stringData) {
          this.redoStack = []
        }

        if (this.initialTemplateSnapshot) {
          this.isDirty = this.templateSnapshot() !== this.initialTemplateSnapshot
        }
      }
    },
    setDefaultAreaSize (area, type) {
      const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
      const pageMask = documentRef.pageRefs[area.page].$refs.mask

      if (type === 'checkbox' || type === 'radio' || type === 'multiple') {
        area.w = pageMask.clientWidth / 30 / pageMask.clientWidth
        area.h = (pageMask.clientWidth / 30 / pageMask.clientWidth) * (pageMask.clientWidth / pageMask.clientHeight)
      } else if (type === 'image') {
        area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
        area.h = (pageMask.clientWidth / 5 / pageMask.clientWidth) * (pageMask.clientWidth / pageMask.clientHeight)
      } else if (type === 'signature' || type === 'stamp' || type === 'verification' || type === 'kba') {
        area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
        area.h = (pageMask.clientWidth / 5 / pageMask.clientWidth) * (pageMask.clientWidth / pageMask.clientHeight) / 2
      } else if (type === 'initials') {
        area.w = pageMask.clientWidth / 10 / pageMask.clientWidth
        area.h = (pageMask.clientWidth / 35 / pageMask.clientWidth)
      } else if (type === 'strikethrough') {
        area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
        area.h = (pageMask.clientWidth / 70 / pageMask.clientWidth)
      } else {
        area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
        area.h = (pageMask.clientWidth / 35 / pageMask.clientWidth)
      }
    },
    onDraw ({ area, isTooSmall }) {
      if (this.drawField) {
        if (this.drawOption) {
          const areaWithoutOption = this.drawField.areas?.find((a) => !a.option_uuid)

          if (areaWithoutOption && !this.drawField.areas.find((a) => a.option_uuid === this.drawField.options[0].uuid)) {
            areaWithoutOption.option_uuid = this.drawField.options[0].uuid
          }

          area.option_uuid = this.drawOption.uuid
        }

        if (area.w === 0 || area.h === 0) {
          const previousArea = this.drawField.areas?.[this.drawField.areas.length - 1]

          if (this.selectedField?.type === this.drawField.type) {
            area.w = this.lastSelectedArea.w
            area.h = this.lastSelectedArea.h
          } else if (previousArea) {
            area.w = previousArea.w
            area.h = previousArea.h
          } else {
            this.setDefaultAreaSize(area, this.drawOption ? 'checkbox' : this.drawField?.type)
          }

          area.x -= area.w / 2
          area.y -= area.h / 2
        }

        this.drawField.areas ||= []

        const insertBeforeAreaIndex = this.drawField.areas.findIndex((a) => {
          return a.attachment_uuid === area.attachment_uuid && a.page > area.page
        })

        if (insertBeforeAreaIndex !== -1) {
          this.drawField.areas.splice(insertBeforeAreaIndex, 0, area)
        } else {
          this.drawField.areas.push(area)
        }

        if (this.template.fields.indexOf(this.drawField) === -1) {
          this.insertField(this.drawField)
        }

        this.drawField = null
        this.drawOption = null

        this.selectedAreasRef.value = [area]

        this.save()
      } else {
        const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
        const pageMask = documentRef.pageRefs[area.page].$refs.mask

        let type = (pageMask.clientWidth * area.w) < 35 ? 'checkbox' : 'text'

        if (this.drawFieldType) {
          type = this.drawFieldType
        } else if (this.defaultDrawFieldType && this.defaultDrawFieldType !== 'text') {
          type = this.defaultDrawFieldType
        } else if (this.fieldTypes.length !== 0 && !this.fieldTypes.includes(type)) {
          type = this.fieldTypes[0]
        }

        if (type === 'checkbox' && !this.drawFieldType && (this.template.fields[this.template.fields.length - 1]?.type === 'checkbox' || area.w)) {
          const previousField = [...this.template.fields].reverse().find((f) => f.type === type)
          const previousArea = previousField?.areas?.[previousField.areas.length - 1]

          if (previousArea || area.w) {
            const areaW = previousArea?.w || area.w || (30 / pageMask.clientWidth)
            const areaH = previousArea?.h || area.h || (30 / pageMask.clientHeight)

            if ((pageMask.clientWidth * area.w) < 5) {
              area.x = area.x - (areaW / 2)
              area.y = area.y - (areaH / 2)
            }

            area.w = areaW
            area.h = areaH
          }
        }

        if (this.drawFieldType && (area.w === 0 || area.h === 0)) {
          if (this.selectedField?.type === this.drawFieldType) {
            area.w = this.lastSelectedArea.w
            area.h = this.lastSelectedArea.h
          } else {
            this.setDefaultAreaSize(area, this.drawFieldType)
          }

          area.x -= area.w / 2
          area.y -= area.h / 2
        }

        if (area.w && (type !== 'checkbox' || this.drawFieldType || !isTooSmall)) {
          this.addField(type, area)

          this.selectedAreasRef.value = [area]
        }
      }
    },
    onDropfield (area) {
      if (this.$refs.dragPlaceholder) {
        this.$refs.dragPlaceholder.dragPlaceholder = null
      }

      if (!this.editable) {
        return
      }

      const field = this.fieldsDragFieldRef.value || {
        name: '',
        uuid: v4(),
        submitter_uuid: this.selectedSubmitter.uuid,
        required: this.dragField.type !== 'checkbox',
        ...this.dragField
      }

      if (!field.type) {
        field.type = 'text'
      }

      if (this.isCohortProgramme && field.type === 'file' && this.selectedSubmitter?.uuid !== 'student') {
        alert('File attachments are only applicable to learners (Student role) in cohort programmes.')
        return
      }

      if (!this.fieldsDragFieldRef.value) {
        if (['select', 'multiple', 'radio'].includes(field.type)) {
          if (this.dragField?.options?.length) {
            field.options = this.dragField.options.map(option => ({ value: option, uuid: v4() }))
          } else {
            field.options = [{ value: '', uuid: v4() }, { value: '', uuid: v4() }]
          }
        }

        if (field.type === 'datenow') {
          field.type = 'date'
          field.readonly = true
          field.default_value = '{{date}}'
        }

        if (['heading', 'strikethrough'].includes(field.type)) {
          field.readonly = true

          if (field.type === 'strikethrough') {
            field.default_value = true
          }
        }

        if (field.type === 'date') {
          field.preferences ||= {}
          field.preferences.format ||= this.defaultDateFormat
        }

        if (field.type === 'signature' && [true, false].includes(this.withSignatureId)) {
          field.preferences ||= {}
          field.preferences.with_signature_id = this.withSignatureId
        }
      }

      const fieldArea = {
        x: (area.x - 6) / area.maskW,
        y: area.y / area.maskH,
        page: area.page,
        attachment_uuid: area.attachment_uuid
      }

      this.assignDropAreaSize(fieldArea, field, area)

      if (field.width) {
        delete field.width
      }

      if (field.height) {
        delete field.height
      }

      field.areas ||= []

      field.areas.push(fieldArea)

      if (this.selectedAreasRef.value.length < 2) {
        this.selectedAreasRef.value = [fieldArea]
      } else {
        this.selectedAreasRef.value.push(fieldArea)
      }

      if (this.template.fields.indexOf(field) === -1) {
        this.insertField(field)
      }

      this.save()

      document.activeElement?.blur()

      if (field.type === 'heading') {
        this.$nextTick(() => {
          const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
          const areaRef = documentRef.pageRefs[area.page].areaRefs.find((ref) => ref.area === fieldArea)

          areaRef.isHeadingSelected = true

          areaRef.focusValueInput()
        })
      }
    },
    assignDropAreaSize (fieldArea, field, area) {
      const fieldType = field.type || 'text'

      const previousField = [...this.template.fields].reverse().find((f) => f.type === fieldType)

      let baseArea

      if (this.selectedField?.type === fieldType) {
        baseArea = this.lastSelectedArea
      } else if (previousField?.areas?.length) {
        baseArea = previousField.areas[previousField.areas.length - 1]
      } else {
        if (['checkbox', 'radio', 'multiple'].includes(fieldType)) {
          baseArea = {
            w: area.maskW / 30 / area.maskW,
            h: area.maskW / 30 / area.maskW * (area.maskW / area.maskH)
          }
        } else if (fieldType === 'image') {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: (area.maskW / 5 / area.maskW) * (area.maskW / area.maskH)
          }
        } else if (fieldType === 'signature' || fieldType === 'stamp' || fieldType === 'verification' || fieldType === 'kba') {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: (area.maskW / 5 / area.maskW) * (area.maskW / area.maskH) / 2
          }
        } else if (fieldType === 'initials') {
          baseArea = {
            w: area.maskW / 10 / area.maskW,
            h: area.maskW / 35 / area.maskW
          }
        } else if (fieldType === 'strikethrough') {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: area.maskW / 70 / area.maskW
          }
        } else {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: area.maskW / 35 / area.maskW
          }
        }
      }

      fieldArea.w = baseArea.w
      fieldArea.h = baseArea.h

      if (fieldType === 'cells') {
        fieldArea.cell_w = baseArea.cell_w || (baseArea.w / 5)
      }

      if (field.areas?.length) {
        const lastArea = field.areas[field.areas.length - 1]

        if (lastArea) {
          fieldArea.w = lastArea.w
          fieldArea.h = lastArea.h
        }
      }

      if (field.width) {
        fieldArea.w = field.width / area.maskW
      }

      if (field.height) {
        fieldArea.h = field.height / area.maskH
      }

      fieldArea.y = fieldArea.y - fieldArea.h / 2
    },
    addBlankPage () {
      this.isLoadingBlankPage = true

      const canvas = document.createElement('canvas')

      canvas.width = 816
      canvas.height = 1056

      const ctx = canvas.getContext('2d')

      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      canvas.toBlob((blob) => {
        const file = new File([blob], `Page ${this.template.schema.length + 1}.png`, { type: blob.type, lastModified: Date.now() })

        const formData = new FormData()
        formData.append('files[]', file)

        this.baseFetch(`/templates/${this.template.id}/documents`, {
          method: 'POST',
          body: formData
        }).then(async (resp) => {
          this.updateFromUpload(await resp.json())
        }).finally(() => {
          this.isLoadingBlankPage = false
        })
      }, 'image/png')
    },
    onUploadFailed (error) {
      if (error) alert(error)
    },
    updateFromUpload (data) {
      this.template.schema.push(...data.schema)
      this.template.documents.push(...data.documents)

      if (data.fields) {
        this.template.fields = data.fields
      }

      if (data.submitters) {
        this.template.submitters = data.submitters

        if (!this.template.submitters.find((s) => s.uuid === this.selectedSubmitter?.uuid)) {
          this.selectedSubmitter = this.template.submitters[0]
        }
      }

      this.$nextTick(() => {
        if (this.$refs.previews) {
          this.$refs.previews.scrollTop = this.$refs.previews.scrollHeight
        }

        this.scrollIntoDocument(data.schema[0])
      })

      if (this.template.name === 'New Document') {
        this.template.name = this.template.schema[0].name
      }

      if (this.onUpload) {
        this.onUpload(this.template)
      }

      this.save()

      data.documents.forEach((attachment) => {
        if (attachment.metadata?.pdf?.fields?.length) {
          this.pendingFieldAttachmentUuids.push(attachment.uuid)

          attachment.metadata.pdf.fields.forEach((field) => {
            field.submitter_uuid = this.selectedSubmitter.uuid

            this.insertField(field)
          })
        }
      })
    },
    updateName (value) {
      this.template.name = value

      this.save()
    },
    onDocumentRemove (item) {
      if (window.confirm(this.t('are_you_sure_'))) {
        this.template.schema.splice(this.template.schema.indexOf(item), 1)

        const removedFieldUuids = []

        this.template.fields.forEach((field) => {
          [...(field.areas || [])].forEach((area) => {
            if (area.attachment_uuid === item.attachment_uuid) {
              field.areas.splice(field.areas.indexOf(area), 1)

              removedFieldUuids.push(field.uuid)
            }
          })
        })

        this.template.fields = this.template.fields.reduce((acc, f) => {
          if (removedFieldUuids.includes(f.uuid) && !f.areas?.length) {
            this.removeFieldConditions(f)
          } else {
            acc.push(f)
          }

          return acc
        }, [])

        this.save()
      }
    },
    onDocumentReplace (data) {
      const { replaceSchemaItem, schema, documents } = data
      // eslint-disable-next-line camelcase
      const { google_drive_file_id, ...cleanedReplaceSchemaItem } = replaceSchemaItem

      this.template.schema.splice(this.template.schema.indexOf(replaceSchemaItem), 1, { ...cleanedReplaceSchemaItem, ...schema[0] })
      this.template.documents.push(...documents)

      if (data.fields) {
        this.template.fields = data.fields

        const removedFieldUuids = []

        this.template.fields.forEach((field) => {
          [...(field.areas || [])].forEach((area) => {
            if (area.attachment_uuid === replaceSchemaItem.attachment_uuid) {
              field.areas.splice(field.areas.indexOf(area), 1)

              removedFieldUuids.push(field.uuid)
            }
          })
        })

        this.template.fields =
          this.template.fields.filter((f) => !removedFieldUuids.includes(f.uuid) || f.areas?.length)
      }

      if (data.submitters) {
        this.template.submitters = data.submitters

        if (!this.template.submitters.find((s) => s.uuid === this.selectedSubmitter?.uuid)) {
          this.selectedSubmitter = this.template.submitters[0]
        }
      }

      this.template.fields.forEach((field) => {
        (field.areas || []).forEach((area) => {
          if (area.attachment_uuid === replaceSchemaItem.attachment_uuid) {
            area.attachment_uuid = schema[0].attachment_uuid
          }
        })
      })

      if (this.onUpload) {
        this.onUpload(this.template)
      }

      this.save()
    },
    onDocumentsReplace (data) {
      data.schema.forEach((schemaItem, index) => {
        const existingSchemaItem = this.template.schema[index]

        if (this.template.schema[index]) {
          this.onDocumentReplace({
            replaceSchemaItem: existingSchemaItem,
            schema: [schemaItem],
            documents: [data.documents.find((doc) => doc.uuid === schemaItem.attachment_uuid)]
          })
        } else {
          this.updateFromUpload({
            schema: [schemaItem],
            documents: [data.documents.find((doc) => doc.uuid === schemaItem.attachment_uuid)],
            fields: data.fields,
            submitters: data.submitters
          })
        }
      })
    },
    onDocumentsReplaceAndTemplateClone (template) {
      window.Turbo.visit(template.is_reusable ? `/templates/${template.id}/edit` : `/documents/${template.id}/edit`)
    },
    moveDocument (item, direction) {
      const currentIndex = this.template.schema.indexOf(item)

      this.template.schema.splice(currentIndex, 1)

      if (currentIndex + direction > this.template.schema.length) {
        this.template.schema.unshift(item)
      } else if (currentIndex + direction < 0) {
        this.template.schema.push(item)
      } else {
        this.template.schema.splice(currentIndex + direction, 0, item)
      }

      this.save()
    },
    maybeShowErrorTemplateAlert (e) {
      if (!this.isAllRequiredFieldsAdded) {
        e.preventDefault()

        const fields = this.defaultRequiredFields?.filter((f) => {
          return !this.template.fields?.some((field) => field.name === f.name)
        })

        if (fields?.length) {
          return alert(this.t('add_all_required_fields_to_continue') + ': ' + fields.map((f) => f.name).join(', '))
        }
      }

      const unmappedSubmitters = this.template.submitters.filter(
        (submitter) => !this.template.fields.some((f) => f.submitter_uuid === submitter.uuid)
      )

      if (unmappedSubmitters.length > 0) {
        e.preventDefault()
        this.isUnmappedSignatoriesModalOpen = true
        return
      }

      if (!this.template.fields.length) {
        e.preventDefault()
        alert(this.t('please_draw_fields_to_prepare_the_document'))
      }
    },
    onSaveClick () {
      if (!this.template.submitters || !this.template.submitters.length) {
        return alert('You have not added any signatories.')
      }

      if (!this.isAllRequiredFieldsAdded) {
        const fields = this.defaultRequiredFields?.filter((f) => {
          return !this.template.fields?.some((field) => field.name === f.name)
        })

        if (fields?.length) {
          return alert(this.t('add_all_required_fields_to_continue') + ': ' + fields.map((f) => f.name).join(', '))
        }
      }

      const unmappedSubmitters = this.template.submitters.filter(
        (submitter) => !this.template.fields.some((f) => f.submitter_uuid === submitter.uuid)
      )

      if (unmappedSubmitters.length > 0) {
        this.isUnmappedSignatoriesModalOpen = true
        return
      }

      if (!this.template.fields.length) {
        return alert(this.t('please_draw_fields_to_prepare_the_document'))
      }

      // For non-reusable Folios (not in cohort programme context), enforce that all mapped submitters have emails attached
      if (!this.template.is_reusable && !this.isCohortProgramme) {
        const submittersWithoutEmail = this.template.submitters.filter((s) => !s.email || !s.email.trim())
        if (submittersWithoutEmail.length > 0) {
          this.isFolioSignatoriesModalOpen = true
          return
        }
      }

      this.executeSaveAndRedirect()
    },
    onContinueMapping (submitter) {
      this.isUnmappedSignatoriesModalOpen = false
      if (submitter) {
        this.selectedSubmitter = submitter
      }
    },
    onRemoveUnmappedSignatories () {
      this.isUnmappedSignatoriesModalOpen = false
      this.template.submitters = this.template.submitters.filter(
        (submitter) => this.template.fields.some((f) => f.submitter_uuid === submitter.uuid)
      )
      this.save()
      this.onSaveClick()
    },
    onSignFolioClick () {
      this.closeDropdown()
      this._pendingSignImmediately = true
      this.onSaveClick()
    },
    onSaveSignatories (updatedSubmitters) {
      this.template.submitters = updatedSubmitters
      this.isFolioSignatoriesModalOpen = false
      this.save()
    },
    executeSaveAndRedirect () {
      this.isSaving = true
      const shouldSign = this._pendingSignImmediately
      this._pendingSignImmediately = false

      this.save({ force: true }).then(() => {
        let redirectPath = this.template.is_reusable ? '/documents?tab=templates' : `/documents/${this.template.id}`

        if (this.isCohortProgramme) {
          redirectPath = shouldSign
            ? `/cohorts/${this.cohortId}/programmes/${this.programmeId}/sign`
            : `/cohorts/${this.cohortId}/programmes/${this.programmeId}`
        } else if (shouldSign && !this.template.is_reusable) {
          redirectPath = `/documents/${this.template.id}?sign=true`
        }

        window.Turbo.visit(redirectPath)
      }).finally(() => {
        this.isSaving = false
      })
    },
    scrollToArea (area) {
      const documentRef = this.documentRefs.find((a) => a.document.uuid === area.attachment_uuid)

      documentRef.scrollToArea(area)

      this.selectedAreasRef.value = [area]
    },
    baseFetch (path, options = {}) {
      return fetch(this.baseUrl + path, {
        ...options,
        headers: {
          'X-CSRF-Token': this.authenticityToken,
          'ngrok-skip-browser-warning': 'true',
          ...this.fetchOptions.headers,
          ...options.headers
        }
      })
    },
    detectFieldsForPage ({ page, attachmentUuid }) {
      this.isDetectingPageFields = true
      this.detectingAnalyzingProgress = null
      this.detectingFieldsAddedCount = null

      let totalFieldsAdded = 0
      const hadFieldsBeforeDetection = this.template.fields.length > 0

      const calculateIoU = (area1, area2) => {
        const x1 = Math.max(area1.x, area2.x)
        const y1 = Math.max(area1.y, area2.y)
        const x2 = Math.min(area1.x + area1.w, area2.x + area2.w)
        const y2 = Math.min(area1.y + area1.h, area2.y + area2.h)

        const intersectionArea = Math.max(0, x2 - x1) * Math.max(0, y2 - y1)
        const area1Size = area1.w * area1.h
        const area2Size = area2.w * area2.h
        const unionArea = area1Size + area2Size - intersectionArea

        return unionArea > 0 ? intersectionArea / unionArea : 0
      }

      const hasOverlappingField = (newArea) => {
        const pageAreas = this.fieldAreasIndex[newArea.attachment_uuid]?.[newArea.page] || []

        return pageAreas.some(({ area: existingArea }) => {
          return calculateIoU(existingArea, newArea) >= 0.1
        })
      }

      const filterNonOverlappingFields = (detectedFields) => {
        return detectedFields.filter((field) => {
          return (field.areas || []).every((area) => !hasOverlappingField(area))
        })
      }

      this.baseFetch(`/templates/${this.template.id}/detect_fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attachment_uuid: attachmentUuid, page })
      }).then(async (response) => {
        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        const fields = []

        while (true) {
          const { value, done } = await reader.read()

          buffer += decoder.decode(value, { stream: true })

          const lines = buffer.split('\n\n')

          buffer = lines.pop()

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.replace(/^data: /, '')
              const data = JSON.parse(jsonStr)

              if (data.error) {
                const errorFields = filterNonOverlappingFields(data.fields || fields)

                if (errorFields.length) {
                  errorFields.forEach((f) => {
                    if (!f.submitter_uuid) {
                      f.submitter_uuid = this.template.submitters[0].uuid
                    }
                    this.insertField(f)
                  })

                  totalFieldsAdded += errorFields.length

                  this.save()
                } else if (!(data.fields || fields).length) {
                  alert(data.error)
                }

                break
              } else if (data.analyzing) {
                this.detectingAnalyzingProgress = data.progress
              } else if (data.completed) {
                if (data.submitters) {
                  if (!hadFieldsBeforeDetection) {
                    this.template.submitters = data.submitters
                    this.selectedSubmitter = this.template.submitters[0]

                    const finalFields = data.fields || fields

                    finalFields.forEach((f) => {
                      if (!f.submitter_uuid) {
                        f.submitter_uuid = this.template.submitters[0].uuid
                      }
                    })

                    const nonOverlappingFields = filterNonOverlappingFields(finalFields)

                    nonOverlappingFields.forEach((f) => this.insertField(f))
                    totalFieldsAdded += nonOverlappingFields.length

                    if (nonOverlappingFields.length) {
                      this.save()
                    }
                  } else {
                    const existingSubmitters = this.template.submitters
                    const submitterUuidMap = {}

                    data.submitters.forEach((newSubmitter) => {
                      const existingMatch = existingSubmitters.find(
                        (s) => s.name.toLowerCase() === newSubmitter.name.toLowerCase()
                      )

                      if (existingMatch) {
                        submitterUuidMap[newSubmitter.uuid] = existingMatch.uuid
                      } else {
                        submitterUuidMap[newSubmitter.uuid] = newSubmitter.uuid

                        if (!existingSubmitters.find((s) => s.uuid === newSubmitter.uuid)) {
                          this.template.submitters.push(newSubmitter)
                        }
                      }
                    })

                    const finalFields = data.fields || fields

                    finalFields.forEach((f) => {
                      if (f.submitter_uuid && submitterUuidMap[f.submitter_uuid]) {
                        f.submitter_uuid = submitterUuidMap[f.submitter_uuid]
                      } else if (!f.submitter_uuid) {
                        f.submitter_uuid = this.template.submitters[0].uuid
                      }
                    })

                    const nonOverlappingFields = filterNonOverlappingFields(finalFields)

                    nonOverlappingFields.forEach((f) => this.insertField(f))
                    totalFieldsAdded += nonOverlappingFields.length

                    if (nonOverlappingFields.length) {
                      this.save()
                    }
                  }
                } else {
                  const finalFields = data.fields || fields

                  finalFields.forEach((f) => {
                    if (!f.submitter_uuid) {
                      f.submitter_uuid = this.template.submitters[0].uuid
                    }
                  })

                  const nonOverlappingFields = filterNonOverlappingFields(finalFields)

                  nonOverlappingFields.forEach((f) => this.insertField(f))
                  totalFieldsAdded += nonOverlappingFields.length

                  if (nonOverlappingFields.length) {
                    this.save()
                  }
                }

                break
              } else if (data.fields) {
                data.fields.forEach((f) => {
                  if (!f.submitter_uuid) {
                    f.submitter_uuid = this.template.submitters[0].uuid
                  }
                })

                fields.push(...data.fields)
              }
            }
          }

          if (done) break
        }
      }).catch(error => {
        console.error('Error in streaming message: ', error)
      }).finally(() => {
        this.isDetectingPageFields = false
        this.detectingAnalyzingProgress = null
        this.detectingFieldsAddedCount = totalFieldsAdded

        setTimeout(() => {
          this.detectingFieldsAddedCount = null
        }, 1000)
      })
    },
    save ({ force } = { force: false }) {
      this.pendingFieldAttachmentUuids = []

      if (this.onChange) {
        this.onChange(this.template)
      }

      if (!this.autosave && !force) {
        return Promise.resolve({})
      }

      this.$nextTick(() => {
        if (this.$el.closest('template-builder')) {
          this.$el.closest('template-builder').dataset.template = JSON.stringify(this.template)
        }
      })

      this.pushUndo()

      return this.baseFetch(`/templates/${this.template.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          template: {
            name: this.template.name,
            schema: this.template.schema,
            submitters: this.template.submitters,
            fields: this.template.fields,
            preferences: this.template.preferences
          }
        }),
        headers: { 'Content-Type': 'application/json' }
      }).then(() => {
        this.initialTemplateSnapshot = this.templateSnapshot()
        this.isDirty = false
        if (this.onSave) {
          this.onSave(this.template)
        }
      })
    }
  }
}
</script>

<style scoped>
/* Force single-line truncation for the document/template title in the builder header */
.template-name :deep(.group\/contenteditable) {
  max-width: 20ch;
  overflow: hidden;
}
.template-name :deep(span[contenteditable]) {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  display: block !important;
  max-width: 100%;
}
</style>
